import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
} from "react";
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Stack,
  TextField,
  Grid,
  Skeleton,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  alpha,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Alert,
  Backdrop,
  CircularProgress,
  InputAdornment,
  Tooltip,
  useTheme,
  Autocomplete,
  Fade,
  TableFooter,
  Avatar,
} from "@mui/material";
import {
  History as HistoryIcon,
  PictureAsPdf as PdfIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  CalendarToday,
  Refresh as RefreshIcon,
  Print as PrintIcon,
  Cancel as CancelIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  Clear as ClearIcon,
  TrendingUp as TrendingUpIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  Inventory as InventoryIcon,
  ArrowUpward as ArrowUpwardIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  ArrowForward as ArrowForwardIcon,
  Person as PersonIcon,
  LocalAtm as LocalAtmIcon,
  AccountBalance as AccountBalanceIcon,
  Paid as PaidIcon,
  Discount as DiscountIcon,
  ErrorOutline as ErrorOutlineIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  FirstPage,
  LastPage,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, parse } from "date-fns";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import * as XLSX from "xlsx";
import { DataContext } from "../../context/DataContext";
import {
  generateTransactionBillHTML,
  // getBillStyles,
} from "../../components/billPrintUtils";

// Skeleton Components for Transaction History

// Header Skeleton
const HeaderSkeleton = ({ isDark }) => (
  <Box
    sx={{
      mb: 4,
      display: "flex",
      flexDirection: { xs: "column", md: "row" },
      justifyContent: "space-between",
      alignItems: { xs: "stretch", md: "center" },
      gap: 3,
    }}
  >
    <Box>
      <Skeleton
        variant="text"
        width={300}
        height={48}
        sx={{ mb: 0.5, background: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)" }}
      />
      <Skeleton
        variant="text"
        width={250}
        height={28}
        sx={{ background: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)" }}
      />
    </Box>
    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
      <Skeleton variant="rounded" width={100} height={36} sx={{ borderRadius: "10px" }} />
      <Skeleton variant="rounded" width={120} height={36} sx={{ borderRadius: "10px" }} />
      <Skeleton variant="rounded" width={120} height={36} sx={{ borderRadius: "10px" }} />
      <Skeleton variant="rounded" width={100} height={36} sx={{ borderRadius: "10px" }} />
    </Box>
  </Box>
);

// Stats Cards Skeleton
const StatsCardsSkeleton = ({ isDark }) => (
  <Grid container spacing={2} sx={{ mb: 4 }}>
    {[1, 2, 3, 4].map((item) => (
      <Grid item xs={12} sm={6} md={3} key={item}>
        <Card
          sx={{
            borderRadius: "16px",
            background: isDark
              ? "linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.6))"
              : "linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6))",
            backdropFilter: "blur(10px)",
            border: isDark
              ? "1px solid rgba(255, 255, 255, 0.1)"
              : "1px solid rgba(0, 0, 0, 0.08)",
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
              <Skeleton variant="text" width={120} height={20} />
            </Box>
            <Skeleton variant="text" width={100} height={40} sx={{ mb: 1 }} />
            <Skeleton variant="text" width={80} height={16} />
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);

// Filters Skeleton
const FiltersSkeleton = ({ isDark }) => (
  <Card
    sx={{
      mb: 4,
      borderRadius: "16px",
      background: isDark
        ? "linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.6))"
        : "linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6))",
      backdropFilter: "blur(10px)",
      border: isDark
        ? "1px solid rgba(255, 255, 255, 0.1)"
        : "1px solid rgba(0, 0, 0, 0.08)",
    }}
  >
    <CardContent sx={{ p: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Skeleton variant="rounded" height={56} width={250} sx={{ borderRadius: "10px" }} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Skeleton variant="rounded" height={56} width={250} sx={{ borderRadius: "10px" }} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Skeleton variant="rounded" height={40} width={250} sx={{ borderRadius: "10px" }} />
        </Grid>
      </Grid>
    </CardContent>
  </Card>
);

// Table Row Skeleton
const TableRowSkeleton = ({ isDark }) => (
  <TableRow>
    <TableCell sx={{ py: 1.5 }}>
      <Skeleton variant="text" width={30} height={24} />
    </TableCell>
    <TableCell sx={{ py: 1.5 }}>
      <Skeleton variant="text" width={100} height={24} />
    </TableCell>
    <TableCell sx={{ py: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Skeleton variant="circular" width={40} height={40} sx={{ borderRadius: "12px" }} />
        <Box>
          <Skeleton variant="text" width={150} height={20} />
          <Skeleton variant="text" width={100} height={16} />
        </Box>
      </Box>
    </TableCell>
    <TableCell sx={{ py: 1.5 }}>
      <Skeleton variant="text" width={80} height={24} />
    </TableCell>
    <TableCell align="center" sx={{ py: 1.5 }}>
      <Skeleton variant="rounded" width={80} height={28} sx={{ borderRadius: "8px" }} />
    </TableCell>
    <TableCell align="right" sx={{ py: 1.5 }}>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
        <Skeleton variant="text" width={100} height={24} />
        <Skeleton variant="text" width={70} height={16} />
      </Box>
    </TableCell>
    <TableCell align="center" sx={{ py: 1.5 }}>
      <Skeleton variant="rounded" width={80} height={28} sx={{ borderRadius: "8px" }} />
    </TableCell>
    <TableCell align="center" sx={{ py: 1.5 }}>
      <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
        <Skeleton variant="circular" width={32} height={32} />
        <Skeleton variant="circular" width={32} height={32} />
        <Skeleton variant="circular" width={32} height={32} />
      </Box>
    </TableCell>
  </TableRow>
);

// Table Header Skeleton
const TableHeaderSkeleton = ({ isDark }) => (
  <TableHead>
    <TableRow>
      {["#", "DATE", "CUSTOMER", "BILL NO", "ITEMS", "AMOUNT", "STATUS", "ACTIONS"].map((header, idx) => (
        <TableCell
          key={idx}
          sx={{
            backgroundColor: isDark ? "rgba(30, 41, 59, 0.95)" : "rgba(248, 250, 252, 0.95)",
            fontWeight: 700,
            color: isDark ? "#94A3B8" : "#64748B",
            fontSize: "0.75rem",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            borderBottom: `2px solid ${
              isDark ? "rgba(71, 85, 105, 0.3)" : "rgba(226, 232, 240, 0.4)"
            }`,
            py: 1.5,
          }}
        >
          <Skeleton variant="text" width={header === "CUSTOMER" ? 80 : 60} height={20} />
        </TableCell>
      ))}
    </TableRow>
  </TableHead>
);

// Table Skeleton
const TableSkeleton = ({ isDark, rows = 5 }) => (
  <Card
    sx={{
      borderRadius: "16px",
      overflow: "hidden",
      position: "relative",
      background: isDark
        ? "linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.2))"
        : "linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.2))",
      backdropFilter: "blur(10px)",
      border: isDark
        ? "1px solid rgba(255, 255, 255, 0.1)"
        : "1px solid rgba(0, 0, 0, 0.08)",
    }}
  >
    <LinearProgress
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 2,
        height: 3,
        "& .MuiLinearProgress-bar": {
          background: "linear-gradient(90deg, #2563EB, #3B82F6, #60A5FA)",
        },
      }}
    />
    <TableContainer sx={{ maxHeight: "calc(100vh - 200px)", overflow: "auto" }}>
      <Table stickyHeader>
        <TableHeaderSkeleton isDark={isDark} />
        <TableBody>
          {[...Array(rows)].map((_, idx) => (
            <TableRowSkeleton key={idx} isDark={isDark} />
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell colSpan={8}>
              <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", p: 2, gap: 2 }}>
                <Skeleton variant="rounded" width={80} height={36} />
                <Skeleton variant="rounded" width={80} height={36} />
                <Skeleton variant="rounded" width={60} height={36} />
              </Box>
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  </Card>
);

// Active Filters Skeleton
// const ActiveFiltersSkeleton = ({ isDark }) => (
//   <Box
//     sx={{
//       mb: 3,
//       display: "flex",
//       alignItems: "center",
//       gap: 1,
//       flexWrap: "wrap",
//       padding: 2,
//       borderRadius: "12px",
//       background: isDark
//         ? "linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.6))"
//         : "linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6))",
//       backdropFilter: "blur(10px)",
//       border: isDark
//         ? "1px solid rgba(255, 255, 255, 0.1)"
//         : "1px solid rgba(0, 0, 0, 0.08)",
//     }}
//   >
//     <Skeleton variant="circular" width={16} height={16} />
//     <Skeleton variant="text" width={80} height={20} />
//     <Skeleton variant="rounded" width={120} height={28} sx={{ borderRadius: "20px" }} />
//     <Skeleton variant="rounded" width={100} height={28} sx={{ borderRadius: "20px" }} />
//     <Skeleton variant="text" width={200} height={20} sx={{ ml: "auto" }} />
//   </Box>
// );

// Complete Page Skeleton
const TransactionHistorySkeleton = ({ isDark }) => {
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ minHeight: "100vh", py: 4 }}>
        <Container maxWidth="xl">
          {/* Alert Skeleton */}
          <Box sx={{ mb: 3, height: 0 }} />

          {/* Header Skeleton */}
          <HeaderSkeleton isDark={isDark} />

          {/* Stats Cards Skeleton */}
          <StatsCardsSkeleton isDark={isDark} />

          {/* Active Filters Skeleton - Optional, shows randomly */}
          {/* <ActiveFiltersSkeleton isDark={isDark} /> */}

          {/* Filters Skeleton */}
          <FiltersSkeleton isDark={isDark} />

          {/* Table Skeleton */}
          <TableSkeleton isDark={isDark} rows={8} />
        </Container>
      </Box>
    </LocalizationProvider>
  );
};

// Custom Hooks
const useDebounce = (value, delay = 300) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

// Table Pagination Actions Component
function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
        size="small"
        sx={{
          color: theme.palette.mode === "dark" ? "#94A3B8" : "#6B7280",
          "&:hover": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.05)",
          },
        }}
      >
        <FirstPage />
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
        size="small"
        sx={{
          color: theme.palette.mode === "dark" ? "#94A3B8" : "#6B7280",
          "&:hover": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.05)",
          },
        }}
      >
        <KeyboardArrowLeft />
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
        size="small"
        sx={{
          color: theme.palette.mode === "dark" ? "#94A3B8" : "#6B7280",
          "&:hover": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.05)",
          },
        }}
      >
        <KeyboardArrowRight />
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
        size="small"
        sx={{
          color: theme.palette.mode === "dark" ? "#94A3B8" : "#6B7280",
          "&:hover": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.05)",
          },
        }}
      >
        <LastPage />
      </IconButton>
    </Box>
  );
}

// Custom Table Components
const StatusChip = ({ isPaid, isCancelled }) => {
  if (isCancelled) {
    return (
      <Chip
        label="CANCELLED"
        size="small"
        sx={{
          fontWeight: 700,
          borderRadius: "8px",
          minWidth: "100px",
          backgroundColor: "rgba(220, 38, 38, 0.15)",
          border: "1px solid rgba(220, 38, 38, 0.3)",
          color: "#DC2626",
          textDecoration: "line-through",
          "& .MuiChip-label": {
            display: "flex",
            alignItems: "center",
            gap: "4px",
          },
        }}
        icon={<CancelIcon sx={{ fontSize: "14px", color: "#DC2626" }} />}
      />
    );
  }

  return (
    <Chip
      label={isPaid ? "Paid" : "Pending"}
      size="small"
      sx={{
        fontWeight: 600,
        borderRadius: "8px",
        minWidth: "80px",
        backgroundColor: isPaid
          ? "rgba(16, 185, 129, 0.15)"
          : "rgba(245, 158, 11, 0.15)",
        border: isPaid
          ? "1px solid rgba(16, 185, 129, 0.3)"
          : "1px solid rgba(245, 158, 11, 0.3)",
        color: isPaid ? "#10B981" : "#F59E0B",
        "& .MuiChip-label": {
          display: "flex",
          alignItems: "center",
          gap: "4px",
        },
      }}
      icon={
        isPaid ? (
          <CheckCircleIcon sx={{ fontSize: "14px", color: "#10B981" }} />
        ) : (
          <PendingIcon sx={{ fontSize: "14px", color: "#F59E0B" }} />
        )
      }
    />
  );
};

const TransactionHistory = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const {
    customerTransactions,
    customers,
    loading: contextLoading,
    refresh,
    removeCustomerTransactionAndUpdateStock,
    stock,
  } = useContext(DataContext);

  // Table state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState("date");
  const [order, setOrder] = useState("desc");

  // States
  const isPageLoading = false;
  const isSearching = false;
  const isFiltering = false;
  const [isExporting, setIsExporting] = useState(false);
  const [silverItemsArray, setSilverItemsArray] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [customerFilter, setCustomerFilter] = useState("all");
  const [dates, setDates] = useState({
    startDate: null,
    endDate: null,
  });
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [filteredTransactions, setFilteredTransactions] = useState([]);

  // Debounced search
  const searchTerm = useDebounce(searchInput, 300);

  // Initialize default dates (current financial year)
  useEffect(() => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();

    let startDate, endDate;

    if (currentMonth >= 3) {
      startDate = new Date(currentYear, 3, 1);
      endDate = today;
    } else {
      startDate = new Date(currentYear - 1, 3, 1);
      endDate = today;
    }

    setDates({ startDate, endDate });
  }, []);

  // Helper function to check if transaction is cancelled
  const isTransactionCancelled = (transaction) => {
    return (
      transaction?.transactionDetails?.type === "cancelled" ||
      transaction?.status === "cancelled" ||
      transaction?.isCancelled === true ||
      transaction?.transactionDetails?.isCancelled === true ||
      transaction?.cancelledAt !== undefined
    );
  };

  // Apply all filters
  const applyFilters = useCallback(() => {
    if (!customerTransactions || customerTransactions.length === 0) {
      setFilteredTransactions([]);
      return;
    }

    let filtered = [...customerTransactions];

    // 1. Filter by customer
    if (customerFilter !== "all") {
      filtered = filtered.filter(
        (transaction) => transaction.customer === customerFilter
      );
    }

    // 2. Filter by date range
    if (dates.startDate && dates.endDate) {
      const startDateStr = format(dates.startDate, "dd/MM/yyyy");
      const endDateStr = format(dates.endDate, "dd/MM/yyyy");
      filtered = filterByDateRange(filtered, startDateStr, endDateStr);
    }

    // 3. Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((transaction) => {
        const customer = customers.find((c) => c.id === transaction.customer);
        const customerNameMatch = customer?.name?.toLowerCase().includes(term);
        const billNoMatch = transaction.transactionDetails?.billNo
          ?.toString()
          .includes(term);
        const itemsMatch = transaction.items?.some(
          (item) =>
            item.name?.toLowerCase().includes(term) ||
            item.huid?.toLowerCase().includes(term)
        );
        const dateMatch = transaction.date?.toLowerCase().includes(term);

        return customerNameMatch || billNoMatch || itemsMatch || dateMatch;
      });
    }

    // Sort by selected column
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (orderBy) {
        case "date":
          aValue = parseDateForSort(a.date);
          bValue = parseDateForSort(b.date);
          break;
        case "billNo":
          aValue = a.transactionDetails?.billNo || 0;
          bValue = b.transactionDetails?.billNo || 0;
          break;
        case "amount":
          aValue = a.summary?.grandTotal || 0;
          bValue = b.summary?.grandTotal || 0;
          break;
        case "status":
          aValue = a.summary?.pendingAmount === 0 ? 0 : 1;
          bValue = b.summary?.pendingAmount === 0 ? 0 : 1;
          break;
        default:
          aValue = a.date;
          bValue = b.date;
      }

      if (order === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredTransactions(filtered);
    setPage(0); // Reset to first page when filters change
  }, [
    customerTransactions,
    customerFilter,
    dates.startDate,
    dates.endDate,
    searchTerm,
    customers,
    orderBy,
    order,
  ]);

  // Parse date for sorting
  const parseDateForSort = (dateStr) => {
    try {
      return parse(dateStr, "dd/MM/yyyy", new Date());
    } catch {
      return new Date(0);
    }
  };

  // Apply filters whenever any filter criteria changes
  useEffect(() => {
    applyFilters();
  }, [
    customerTransactions,
    customerFilter,
    dates.startDate,
    dates.endDate,
    searchTerm,
    applyFilters,
  ]);

  // React version of the filterByDateRange function
  function filterByDateRange(records, startDate, endDate) {
    if (!records || records.length === 0) return [];
    if (!startDate || !endDate) return records;

    const parseFilterDate = (dateStr) => {
      if (!dateStr) return null;
      if (dateStr instanceof Date) return dateStr;

      const parts = dateStr.split("/");
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);

        if (isNaN(day) || isNaN(month) || isNaN(year)) return null;

        const date = new Date(year, month, day);
        if (
          isNaN(date.getTime()) ||
          date.getDate() !== day ||
          date.getMonth() !== month ||
          date.getFullYear() !== year
        ) {
          return null;
        }
        return date;
      }
      const isoDate = new Date(dateStr);
      return isNaN(isoDate.getTime()) ? null : isoDate;
    };

    const parseRecordDate = (dateStr) => {
      if (!dateStr) return null;
      if (dateStr instanceof Date) return dateStr;

      if (typeof dateStr === "string") {
        const parts = dateStr.split("/");
        if (parts.length === 3) {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1;
          const year = parseInt(parts[2], 10);

          if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
            const date = new Date(year, month, day);
            if (
              !isNaN(date.getTime()) &&
              date.getDate() === day &&
              date.getMonth() === month &&
              date.getFullYear() === year
            ) {
              return date;
            }
          }
        }

        const isoDate = new Date(dateStr);
        if (!isNaN(isoDate.getTime())) return isoDate;
      }
      return null;
    };

    const start = parseFilterDate(startDate);
    const end = parseFilterDate(endDate);

    if (!start || !end) return records;

    const endOfDay = new Date(end);
    endOfDay.setHours(23, 59, 59, 999);

    return records.filter((record) => {
      if (!record.date) return false;
      const recordDate = parseRecordDate(record.date);
      if (!recordDate) return false;
      return recordDate >= start && recordDate <= endOfDay;
    });
  }

  // Show Alert
  const showAlert = useCallback((message, type) => {
    setAlertMessage(message);
    setAlertType(type);
    setTimeout(() => setAlertMessage(""), 3000);
  }, []);

  // Handle Sort
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Handle Print Bill
  // const handlePrintBill = useCallback(
  //   (transaction) => {
  //     if (!transaction) return;

  //     const isGst = transaction.transactionDetails?.gstApplicable;
  //     const billHtml = generateTransactionBillHTML(transaction, customers);
  //     const styles = getBillStyles();

  //     const printWindow = window.open("", "_blank");
  //     if (!printWindow) {
  //       alert("Please allow popups to print the bill");
  //       return;
  //     }

  //     printWindow.document.write(`
  //     <!DOCTYPE html>
  //     <html>
  //       <head>
  //         <title>Print Bill - ${isGst ? "GST Invoice" : "Cash Memo"}</title>
  //         <meta charset="UTF-8">
  //         <meta name="viewport" content="width=device-width, initial-scale=1.0">
  //         <style>
  //           ${styles}
  //           @media print {
  //             body {
  //               -webkit-print-color-adjust: exact !important;
  //               color-adjust: exact !important;
  //               print-color-adjust: exact !important;
  //             }
  //             table {
  //               page-break-inside: avoid !important;
  //               break-inside: avoid !important;
  //             }
  //             tr {
  //               page-break-inside: avoid !important;
  //               page-break-after: auto !important;
  //               break-inside: avoid !important;
  //             }
  //             thead {
  //               display: table-header-group !important;
  //             }
  //             tfoot {
  //               display: table-footer-group !important;
  //             }
  //           }
  //           .watermark {
  //             position: fixed !important;
  //             top: 50% !important;
  //             left: 50% !important;
  //             transform: translate(-50%, -50%) rotate(-45deg) !important;
  //             opacity: 0.1 !important;
  //             z-index: -1 !important;
  //             pointer-events: none !important;
  //           }
  //           .watermark img {
  //             width: 400px !important;
  //             height: 400px !important;
  //             object-fit: contain !important;
  //           }
  //         </style>
  //       </head>
  //       <body>
  //         ${billHtml}
  //         <script>
  //           window.onload = function() {
  //             setTimeout(function() {
  //               window.print();
  //               window.onafterprint = function() {
  //                 setTimeout(() => window.close(), 100);
  //               };
  //             }, 500);
  //           };
  //         </script>
  //       </body>
  //     </html>
  //   `);
  //     printWindow.document.close();
  //   },
  //   [customers]
  // );

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [selectedCancelTransaction, setSelectedCancelTransaction] =
    useState(null);
  const [refundAmounts, setRefundAmounts] = useState({
    cash: 0,
    bank: 0,
    total: 0,
  });
  const [cancellingItems, setCancellingItems] = useState([]);
  const [isCancelling, setIsCancelling] = useState(false);

  const handleRefundChange = (type, value) => {
    const numValue = parseFloat(value) || 0;
    setRefundAmounts((prev) => {
      const updated = {
        ...prev,
        [type]: numValue,
      };
      updated.total = updated.cash + updated.bank;
      return updated;
    });
  };

  const handleCancelModalClose = () => {
    setCancelModalOpen(false);
    setSelectedCancelTransaction(null);
    setRefundAmounts({ cash: 0, bank: 0, total: 0 });
    setCancellingItems([]);
    setSilverItemsArray([]);
  };

  const handleCancelBill = useCallback(
    (transaction) => {
      const parseJewelryName = (name) => {
        if (!name || typeof name !== "string") {
          return {
            type: "",
            serialNo: "",
            metal: "",
            purity: "",
            originalName: name || "",
            isValid: false,
          };
        }

        const originalName = name.trim();
        let type = "";
        let serialNo = "";
        let metal = "";
        let purity = "";

        const metalPatterns = [
          { pattern: /\b(gold|golden)\b/i, metal: "Gold" },
          { pattern: /\b(silver)\b/i, metal: "Silver" },
          { pattern: /\b(platinum|pt)\b/i, metal: "Platinum" },
          { pattern: /\b(diamond)\b/i, metal: "Diamond" },
        ];

        const purityPatterns = [
          { pattern: /\b(\d+)(?:k|K|carat|ct)\b/, key: "k" },
          { pattern: /\b(\d+)(?:\.\d+)?\s*(?:gram|g|gm)\b/, key: "gram" },
          { pattern: /\b999\b/, key: "999" },
          { pattern: /\b925\b/, key: "925" },
        ];

        const jewelryTypes = [
          "Finger Ring",
          "Bangles/Kada",
          "Bracelet",
          "Chain",
          "Earrings",
          "Kansar",
          "Mangalsutra",
          "Miscellaneous",
          "Pendent",
          "Necklace",
          "Anklet",
        ];

        let cleanName = originalName;

        for (const { pattern, metal: metalType } of metalPatterns) {
          const match = cleanName.match(pattern);
          if (match) {
            metal = metalType;
            cleanName = cleanName.replace(pattern, "").trim();
            break;
          }
        }

        for (const { pattern, key } of purityPatterns) {
          const match = cleanName.match(pattern);
          if (match) {
            if (key === "k") {
              purity = `${match[1]}K`;
            } else if (key === "gram") {
              purity = `${match[1]}g`;
            } else {
              purity = match[0];
            }
            cleanName = cleanName.replace(pattern, "").trim();
            break;
          }
        }

        const pattern1 = /^(.+?)\s*[-–—]\s*(\d+)(?:\s|$)/i;
        const match1 = cleanName.match(pattern1);

        if (match1) {
          type = match1[1].trim();
          serialNo = match1[2].trim();
        } else {
          const pattern2 = /^(.+?)\s+(\d+)(?:\s|$)/i;
          const match2 = cleanName.match(pattern2);

          if (match2) {
            type = match2[1].trim();
            serialNo = match2[2].trim();
          } else {
            const numberMatch = cleanName.match(/\d+/);
            if (numberMatch) {
              serialNo = numberMatch[0];
              type = cleanName
                .replace(numberMatch[0], "")
                .replace(/\s+/g, " ")
                .trim();
            } else {
              type = cleanName;
            }
          }
        }

        type = type.replace(/[\d\-–—]+$/, "").trim();

        const typeMap = {
          fingerring: "Finger Ring",
          earing: "Ear Ring",
          earring: "Ear Ring",
          nosepin: "Nose Pin",
          mangalsutra: "Mangalsutra",
          toering: "Toe Ring",
          tiepin: "Tie Pin",
        };

        const normalizedType = type.toLowerCase().replace(/\s+/g, "");
        if (typeMap[normalizedType]) {
          type = typeMap[normalizedType];
        }

        if (!type || type.length < 2) {
          for (const knownType of jewelryTypes) {
            if (originalName.toLowerCase().includes(knownType.toLowerCase())) {
              type = knownType;
              break;
            }
          }
        }

        return {
          type: type,
          serialNo: serialNo,
          metal: metal,
          purity: purity,
          originalName: originalName,
          isValid: !!type || !!serialNo,
        };
      };

      const findFirstMissingNumber = (arr) => {
        if (arr.length === 0) return 1;
        const numberSet = new Set(arr);
        let i = 1;
        while (numberSet.has(i)) {
          i++;
        }
        return i;
      };

      const getNextSerialNumber = (itemName, metal, currentStock) => {
        if (!itemName || !metal) return 1;
        const allItemsForType = currentStock.filter(
          (item) => item.name === itemName && item.metal === metal
        );
        if (allItemsForType.length === 0) return 1;
        const unsoldItems = allItemsForType.filter(
          (item) => item.sold === "No" || item.sold === false
        );
        const unsoldSerialNumbers = unsoldItems
          .map((item) => parseInt(item.serialNo) || 0)
          .filter((num) => !isNaN(num) && num > 0)
          .sort((a, b) => a - b);
        const firstMissing = findFirstMissingNumber(unsoldSerialNumbers);
        return firstMissing;
      };

      const itemsToCancel = [];
      const silverItems = [];

      for (let i = 0; i < transaction?.items?.length; i++) {
        const item = transaction.items[i];

        if (!item?.manualItemDetails) {
          const parsedInfo = parseJewelryName(item?.name);

          if (parsedInfo.isValid) {
            const matchingItems = stock.filter(
              (stockItem) =>
                stockItem.name === parsedInfo.type &&
                parseInt(stockItem.serialNo) ===
                  parseInt(parsedInfo.serialNo) &&
                stockItem.huid === item.huid &&
                stockItem.sold === "Yes"
            );

            if (matchingItems.length > 0) {
              const itemToUpdate = matchingItems[0];

              const conflictingItems = stock.filter(
                (stockItem) =>
                  stockItem.name === parsedInfo.type &&
                  parseInt(stockItem.serialNo) ===
                    parseInt(parsedInfo.serialNo) &&
                  stockItem.sold === "No" &&
                  stockItem.id !== itemToUpdate.id
              );

              let newSerialNumber = parsedInfo.serialNo;

              if (conflictingItems.length > 0) {
                newSerialNumber = getNextSerialNumber(
                  parsedInfo.type,
                  itemToUpdate.metal,
                  stock
                );
              }

              itemsToCancel.push({
                ...itemToUpdate,
                updateData: {
                  sold: "No",
                  soldDescription: "",
                  serialNo:
                    conflictingItems.length > 0
                      ? newSerialNumber
                      : parsedInfo.serialNo,
                },
                originalSerialNo: parsedInfo.serialNo,
                hasConflict: conflictingItems.length > 0,
                transactionItem: item,
              });
            }
          }
        } else {
          if (
            item?.manualItemDetails?.metal === "Silver" &&
            item?.manualItemDetails?.silverType
          ) {
            const silverItem = {
              silverType: item.manualItemDetails.silverType,
              weight: parseFloat(item.manualItemDetails.weight) || 0,
              transactionItem: item,
            };
            silverItems.push(silverItem);
          }
        }
      }

      const aggregatedSilverItems = {};
      silverItems.forEach((silverItem) => {
        const { silverType, weight, transactionItem } = silverItem;

        if (!aggregatedSilverItems[silverType]) {
          aggregatedSilverItems[silverType] = {
            silverType: silverType,
            totalWeight: 0,
            items: [],
          };
        }

        aggregatedSilverItems[silverType].totalWeight += weight;
        aggregatedSilverItems[silverType].items.push({
          ...silverItem,
          transactionItem: transactionItem,
        });
      });

      const silverItemsArrayResult = Object.values(aggregatedSilverItems);
      const hasJewelryItems = itemsToCancel.length > 0;
      const hasSilverItems = silverItemsArrayResult.length > 0;

      if (hasJewelryItems || hasSilverItems) {
        setSelectedCancelTransaction(transaction);
        setCancellingItems(itemsToCancel);
        setSilverItemsArray(silverItemsArrayResult);

        const paidAmount =
          transaction.summary?.paymentDone ||
          transaction.summary?.grandTotal ||
          0;
        setRefundAmounts({
          cash: paidAmount,
          bank: 0,
          total: paidAmount,
        });

        setCancelModalOpen(true);
      } else {
        alert(
          `No sold items found in bill ${transaction.transactionDetails?.billNo} to cancel.`
        );
      }
    },
    [stock]
  );

  const setCancelModal = useCallback(
    (open, transaction) => {
      setCancelModalOpen(open);
      handleCancelBill(transaction);
    },
    [handleCancelBill]
  );

  const handleConfirmCancellation = async () => {
    setIsCancelling(true);
    try {
      const silverItems = silverItemsArray.map((silverGroup) => ({
        silverType: silverGroup.silverType,
        totalWeight: silverGroup.totalWeight,
        itemCount: silverGroup.items.length,
        items: silverGroup.items.map((item) => ({
          itemNumber: item.transactionItem?.itemNumber || 0,
          weight: item.weight,
          name: item.transactionItem?.name || "Silver Item",
          silverType: item.silverType,
        })),
      }));

      const cancellationData = {
        transactionId: selectedCancelTransaction.id,
        billNo: selectedCancelTransaction.transactionDetails?.billNo,
        customerId: selectedCancelTransaction.customer,
        refund: {
          cash: refundAmounts.cash,
          bank: refundAmounts.bank,
          total: refundAmounts.total,
        },
        items: cancellingItems.map((item) => ({
          id: item.id,
          name: item.name,
          huid: item.huid,
          originalSerialNo: item.originalSerialNo,
          newSerialNo: item.updateData.serialNo,
          hasConflict: item.hasConflict,
          updateData: item.updateData,
        })),
        silverItems: silverItems,
        cancelledAt: new Date().toISOString(),
      };

      await removeCustomerTransactionAndUpdateStock(cancellationData);
      showAlert(
        `Bill ${selectedCancelTransaction.transactionDetails?.billNo} has been cancelled successfully!`,
        "success"
      );
      refresh();
      handleCancelModalClose();
    } catch (error) {
      console.error("Error cancelling bill:", error);
      showAlert("Failed to cancel bill. Please try again.", "error");
    } finally {
      setIsCancelling(false);
    }
  };

  // Handle Export PDF
  const handleExportPDF = useCallback(async () => {
    if (filteredTransactions.length === 0) {
      showAlert("No transactions to export", "warning");
      return;
    }

    setIsExporting(true);

    try {
      showAlert("Generating PDF...", "info");

      const pdf = new jsPDF("p", "mm", "a4");
      const gstTransactions = filteredTransactions.filter(
        (t) => t.transactionDetails?.gstApplicable
      );

      if (gstTransactions.length === 0) {
        showAlert("No GST transactions found for export", "warning");
        return;
      }

      for (let i = 0; i < gstTransactions.length; i++) {
        if (i > 0) pdf.addPage();

        const transaction = gstTransactions[i];
        const billHtml = generateTransactionBillHTML(transaction, customers);

        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = billHtml;
        tempDiv.style.width = "210mm";
        tempDiv.style.padding = "0";
        tempDiv.style.margin = "0";
        document.body.appendChild(tempDiv);

        const canvas = await html2canvas(tempDiv, {
          scale: 2,
          useCORS: true,
          logging: false,
        });

        const imgData = canvas.toDataURL("image/jpeg", 0.9);
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;
        const ratio = imgWidth / imgHeight;

        const margin = 5;
        const maxWidth = pageWidth - 2 * margin;
        const maxHeight = pageHeight - 2 * margin;

        let finalWidth, finalHeight;

        if (imgWidth > imgHeight) {
          finalWidth = maxWidth;
          finalHeight = maxWidth / ratio;
          if (finalHeight > maxHeight) {
            finalHeight = maxHeight;
            finalWidth = maxHeight * ratio;
          }
        } else {
          finalHeight = maxHeight;
          finalWidth = maxHeight * ratio;
          if (finalWidth > maxWidth) {
            finalWidth = maxWidth;
            finalHeight = maxWidth / ratio;
          }
        }

        const x = (pageWidth - finalWidth) / 2;
        const y = (pageHeight - finalHeight) / 2;

        pdf.addImage(imgData, "JPEG", x, y, finalWidth, finalHeight);
        document.body.removeChild(tempDiv);
      }

      const fileName = `Transaction_History_${format(
        dates.startDate,
        "dd-MM-yyyy"
      )}_to_${format(dates.endDate, "dd-MM-yyyy")}.pdf`;
      pdf.save(fileName);

      showAlert("PDF exported successfully", "success");
    } catch (error) {
      console.error("Error generating PDF:", error);
      showAlert("Failed to generate PDF", "error");
    } finally {
      setIsExporting(false);
    }
  }, [dates, customers, showAlert, filteredTransactions]);

  // Handle Export Excel
  const handleExportExcel = useCallback(() => {
    if (filteredTransactions.length === 0) {
      showAlert("No transactions to export", "warning");
      return;
    }

    try {
      const excelData = filteredTransactions.map((transaction, index) => {
        const customer = customers.find((c) => c.id === transaction.customer);
        return {
          "Sr. No.": index + 1,
          Date: transaction.date,
          "Customer Name": customer?.name || "N/A",
          "Bill No": transaction.transactionDetails?.billNo || "N/A",
          "Items Count": transaction.items?.length || 0,
          Subtotal: transaction.calculatedTotals?.subtotal || 0,
          CGST: transaction.summary?.cgst || 0,
          SGST: transaction.summary?.sgst || 0,
          "Grand Total": transaction.summary?.grandTotal || 0,
          Paid: transaction.summary?.paymentDone || 0,
          Pending: transaction.summary?.pendingAmount || 0,
          Status:
            (transaction.summary?.pendingAmount || 0) === 0
              ? "Paid"
              : "Pending",
          GST: transaction.transactionDetails?.gstApplicable ? "Yes" : "No",
        };
      });

      const totals = {
        "Sr. No.": "TOTAL",
        Date: "",
        "Customer Name": "",
        "Bill No": "",
        "Items Count": excelData.reduce(
          (sum, row) => sum + row["Items Count"],
          0
        ),
        Subtotal: excelData.reduce((sum, row) => sum + row["Subtotal"], 0),
        CGST: excelData.reduce((sum, row) => sum + row["CGST"], 0),
        SGST: excelData.reduce((sum, row) => sum + row["SGST"], 0),
        "Grand Total": excelData.reduce(
          (sum, row) => sum + row["Grand Total"],
          0
        ),
        Paid: excelData.reduce((sum, row) => sum + row["Paid"], 0),
        Pending: excelData.reduce((sum, row) => sum + row["Pending"], 0),
        Status: "",
        GST: "",
      };

      excelData.push({});
      excelData.push(totals);

      const headers = Object.keys(excelData[0]);
      const dataWithHeaders = [
        headers,
        ...excelData.map((row) => headers.map((key) => row[key])),
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(dataWithHeaders);

      const colWidths = [
        { wch: 8 },
        { wch: 12 },
        { wch: 25 },
        { wch: 10 },
        { wch: 12 },
        { wch: 15 },
        { wch: 12 },
        { wch: 12 },
        { wch: 15 },
        { wch: 12 },
        { wch: 12 },
        { wch: 10 },
        { wch: 8 },
      ];
      worksheet["!cols"] = colWidths;

      const headerRange = XLSX.utils.decode_range(worksheet["!ref"]);
      headerRange.s.r = 0;
      headerRange.e.r = 0;

      for (let C = headerRange.s.c; C <= headerRange.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
        if (!worksheet[cellAddress]) continue;
        worksheet[cellAddress].s = {
          font: { bold: true },
          fill: { fgColor: { rgb: "E0E0E0" } },
          alignment: { horizontal: "center" },
        };
      }

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Transactions");

      const fileName = `Transaction_History_${format(
        dates.startDate,
        "dd-MM-yyyy"
      )}_to_${format(dates.endDate, "dd-MM-yyyy")}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      showAlert("Excel file exported successfully", "success");
    } catch (error) {
      console.error("Error exporting Excel:", error);
      showAlert("Failed to export Excel file", "error");
    }
  }, [customers, dates, showAlert, filteredTransactions]);

  // Handle Clear Filters
  const handleClearFilters = useCallback(() => {
    setCustomerFilter("all");
    setSearchInput("");
    showAlert("Filters cleared", "info");
  }, [showAlert]);

  // Helper function
  const getCustomerName = (customerId) => {
    const customer = customers.find((c) => c.id === customerId);
    return customer?.name || "N/A";
  };

  // Get customer object
  const getCustomer = (customerId) => {
    return customers.find((c) => c.id === customerId);
  };

  // Loading states
  const isLoading = contextLoading || isPageLoading;
  const isProcessing = isSearching || isFiltering || isExporting;

  const isRefreshing = false;

  // Update the loading condition at the top of your component
  if ( isLoading || isRefreshing) {
    return <TransactionHistorySkeleton isDark={isDark} />;
  }


  // Get current page data
  const paginatedTransactions = filteredTransactions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ minHeight: "100vh", py: 4 }}>
        {/* Backdrop for loading states */}
        <Backdrop
          open={isExporting}
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            color: "#fff",
            backdropFilter: "blur(10px)",
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress color="inherit" size={50} />
            <Typography sx={{ mt: 2, fontWeight: 600 }}>
              Generating PDF...
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
              This may take a moment
            </Typography>
          </Box>
        </Backdrop>

        <Container maxWidth="xl">
          {/* Alert */}
          {alertMessage && (
            <Fade in={!!alertMessage}>
              <Alert
                severity={alertType}
                sx={{
                  mb: 3,
                  borderRadius: "12px",
                  backdropFilter: "blur(10px)",
                  border: isDark
                    ? "1px solid rgba(255, 255, 255, 0.1)"
                    : "1px solid rgba(0, 0, 0, 0.1)",
                  backgroundColor: isDark
                    ? "rgba(30, 41, 59, 0.7)"
                    : "rgba(255, 255, 255, 0.8)",
                }}
                onClose={() => setAlertMessage("")}
              >
                {alertMessage}
              </Alert>
            </Fade>
          )}

          {/* Header */}
          <Box
            sx={{
              mb: 4,
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "stretch", md: "center" },
              gap: 3,
            }}
          >
            <Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 900,
                  mb: 0.5,
                  background: isDark
                    ? "linear-gradient(135deg, #F1F5F9, #E2E8F0)"
                    : "linear-gradient(135deg, #1E293B, #374151)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Transaction History
              </Typography>
              <Typography
                sx={{
                  color: isDark ? "#A0A0A0" : "#6B7280",
                  fontSize: "1.1rem",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <HistoryIcon fontSize="small" />
                View and manage customer transactions
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                justifyContent: { xs: "center", md: "flex-end" },
              }}
            >
              <Button
                startIcon={<RefreshIcon />}
                onClick={refresh}
                variant="outlined"
                disabled={isFiltering}
                sx={{
                  borderColor:
                    theme.palette.mode === "dark" ? "#60A5FA" : "#2563EB",
                  color: theme.palette.mode === "dark" ? "#60A5FA" : "#fff",
                  borderRadius: "10px",
                  backdropFilter: "blur(10px)",
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(30, 41, 59, 0.8)"
                      : "rgba(37, 99, 235, 0.8)",
                  borderWidth: "1.5px",
                  fontWeight: 600,
                  transition: "all 0.2s ease",
                }}
              >
                Refresh
              </Button>
              <Button
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                variant="outlined"
                disabled={customerFilter === "all" && !searchInput}
                sx={{
                  borderColor:
                    theme.palette.mode === "dark" ? "#94A3B8" : "#6B7280",
                  color: theme.palette.mode === "dark" ? "#E2E8F0" : "#fff",
                  borderRadius: "10px",
                  backdropFilter: "blur(10px)",
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(30, 41, 59, 0.8)"
                      : "rgba(107, 114, 128, 0.8)",
                  borderWidth: "1.5px",
                  fontWeight: 600,
                  transition: "all 0.2s ease",
                }}
              >
                Clear Filters
              </Button>
              <Button
                startIcon={<DownloadIcon />}
                onClick={handleExportExcel}
                variant="outlined"
                disabled={filteredTransactions.length === 0}
                sx={{
                  borderColor:
                    theme.palette.mode === "dark" ? "#34D399" : "#10B981",
                  color: theme.palette.mode === "dark" ? "#34D399" : "#fff",
                  borderRadius: "10px",
                  backdropFilter: "blur(10px)",
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(30, 41, 59, 0.8)"
                      : "rgba(16, 185, 129, 0.8)",
                  borderWidth: "1.5px",
                  fontWeight: 600,
                  transition: "all 0.2s ease",
                }}
              >
                Export Excel
              </Button>
              <Button
                startIcon={<PdfIcon />}
                onClick={handleExportPDF}
                variant="contained"
                // disabled={isExporting || filteredTransactions.length === 0}
                disabled
                sx={{
                  background: "linear-gradient(135deg, #DC2626, #B91C1C)",
                  borderRadius: "10px",
                  boxShadow: "0 4px 12px rgba(220, 38, 38, 0.4)",
                  fontWeight: 600,
                  transition: "all 0.2s ease",
                }}
              >
                Export PDF
              </Button>
            </Box>
          </Box>

          {/* Stats Summary */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  borderRadius: "16px",
                  background: isDark
                    ? "linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.6))"
                    : "linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6))",
                  backdropFilter: "blur(10px)",
                  border: isDark
                    ? "1px solid rgba(255, 255, 255, 0.1)"
                    : "1px solid rgba(0, 0, 0, 0.08)",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "10px",
                        background:
                          "linear-gradient(135deg, rgba(37, 99, 235, 0.15), rgba(59, 130, 246, 0.15))",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 2,
                      }}
                    >
                      <ReceiptIcon sx={{ color: "#2563EB" }} />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Total Transactions
                    </Typography>
                  </Box>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 800, color: "#2563EB", mb: 1 }}
                  >
                    {filteredTransactions.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {filteredTransactions.length > 0
                      ? `${(
                          (filteredTransactions.length /
                            customerTransactions.length) *
                          100
                        ).toFixed(1)}% of total`
                      : "No transactions"}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  borderRadius: "16px",
                  background: isDark
                    ? "linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.6))"
                    : "linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6))",
                  backdropFilter: "blur(10px)",
                  border: isDark
                    ? "1px solid rgba(255, 255, 255, 0.1)"
                    : "1px solid rgba(0, 0, 0, 0.08)",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "10px",
                        background:
                          "linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(34, 197, 94, 0.15))",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 2,
                      }}
                    >
                      <TrendingUpIcon sx={{ color: "#10B981" }} />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Total Amount
                    </Typography>
                  </Box>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 800, color: "#10B981", mb: 1 }}
                  >
                    {new Intl.NumberFormat("en-IN", {
                      style: "currency",
                      currency: "INR",
                      minimumFractionDigits: 0,
                    }).format(
                      filteredTransactions.reduce(
                        (sum, t) => sum + parseInt(t.summary?.grandTotal || 0),
                        0
                      )
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Avg:{" "}
                    {new Intl.NumberFormat("en-IN", {
                      style: "currency",
                      currency: "INR",
                      minimumFractionDigits: 0,
                    }).format(
                      filteredTransactions.length > 0
                        ? filteredTransactions.reduce(
                            (sum, t) =>
                              sum + parseInt(t.summary?.grandTotal || 0),
                            0
                          ) / filteredTransactions.length
                        : 0
                    )}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  borderRadius: "16px",
                  background: isDark
                    ? "linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.6))"
                    : "linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6))",
                  backdropFilter: "blur(10px)",
                  border: isDark
                    ? "1px solid rgba(255, 255, 255, 0.1)"
                    : "1px solid rgba(0, 0, 0, 0.08)",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "10px",
                        background:
                          "linear-gradient(135deg, rgba(239, 68, 68, 0.15), rgba(220, 38, 38, 0.15))",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 2,
                      }}
                    >
                      <PaymentIcon sx={{ color: "#EF4444" }} />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Total Pending
                    </Typography>
                  </Box>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 800, color: "#EF4444", mb: 1 }}
                  >
                    {new Intl.NumberFormat("en-IN", {
                      style: "currency",
                      currency: "INR",
                      minimumFractionDigits: 0,
                    }).format(
                      filteredTransactions.reduce(
                        (sum, t) =>
                          sum + parseInt(t.summary?.pendingAmount || 0),
                        0
                      )
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {
                      filteredTransactions.filter(
                        (t) => t.summary?.pendingAmount > 0
                      ).length
                    }{" "}
                    pending
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  borderRadius: "16px",
                  background: isDark
                    ? "linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.6))"
                    : "linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6))",
                  backdropFilter: "blur(10px)",
                  border: isDark
                    ? "1px solid rgba(255, 255, 255, 0.1)"
                    : "1px solid rgba(0, 0, 0, 0.08)",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "10px",
                        background:
                          "linear-gradient(135deg, rgba(34, 197, 94, 0.15), rgba(22, 163, 74, 0.15))",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 2,
                      }}
                    >
                      <CheckCircleIcon sx={{ color: "#22C55E" }} />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Fully Paid
                    </Typography>
                  </Box>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 800, color: "#22C55E", mb: 1 }}
                  >
                    {
                      filteredTransactions.filter(
                        (t) => t.summary?.pendingAmount === 0
                      ).length
                    }
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {filteredTransactions.length > 0
                      ? (
                          (filteredTransactions.filter(
                            (t) => t.summary?.pendingAmount === 0
                          ).length /
                            filteredTransactions.length) *
                          100
                        ).toFixed(1)
                      : 0}
                    % paid
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Active Filters Summary */}
          {(customerFilter !== "all" || searchInput) && (
            <Fade in>
              <Box
                sx={{
                  mb: 3,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                  padding: 2,
                  borderRadius: "12px",
                  background: isDark
                    ? "linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.6))"
                    : "linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6))",
                  backdropFilter: "blur(10px)",
                  border: isDark
                    ? "1px solid rgba(255, 255, 255, 0.1)"
                    : "1px solid rgba(0, 0, 0, 0.08)",
                }}
              >
                <FilterIcon sx={{ color: "#6B7280", fontSize: "1rem" }} />
                <Typography variant="body2" color="text.secondary">
                  Active filters:
                </Typography>
                {customerFilter !== "all" && (
                  <Chip
                    label={`Customer: ${
                      customers.find((c) => c.id === customerFilter)?.name ||
                      "Selected Customer"
                    }`}
                    size="small"
                    onDelete={() => setCustomerFilter("all")}
                    color="primary"
                    variant="filled"
                    sx={{
                      backdropFilter: "blur(10px)",
                      backgroundColor: "rgba(37, 99, 235, 0.1)",
                      border: "1px solid rgba(37, 99, 235, 0.3)",
                      color: "#2563EB",
                      fontWeight: 500,
                    }}
                  />
                )}
                {searchInput && (
                  <Chip
                    label={`Search: "${searchInput}"`}
                    size="small"
                    onDelete={() => setSearchInput("")}
                    color="secondary"
                    variant="filled"
                    sx={{
                      backdropFilter: "blur(10px)",
                      backgroundColor: "rgba(139, 92, 246, 0.1)",
                      border: "1px solid rgba(139, 92, 246, 0.3)",
                      color: "#8B5CF6",
                      fontWeight: 500,
                    }}
                  />
                )}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: "auto" }}
                >
                  Showing {filteredTransactions.length} of{" "}
                  {customerTransactions.length} transactions
                </Typography>
              </Box>
            </Fade>
          )}

          {/* Filters Section */}
          <Card
            sx={{
              mb: 4,
              borderRadius: "16px",
              background: isDark
                ? "linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.6))"
                : "linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6))",
              backdropFilter: "blur(10px)",
              border: isDark
                ? "1px solid rgba(255, 255, 255, 0.1)"
                : "1px solid rgba(0, 0, 0, 0.08)",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="Start Date"
                    value={dates.startDate}
                    onChange={(newValue) =>
                      setDates((prev) => ({ ...prev, startDate: newValue }))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarIcon />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            backdropFilter: "blur(10px)",
                            backgroundColor: isDark
                              ? "rgba(255, 255, 255, 0.05)"
                              : "rgba(0, 0, 0, 0.02)",
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="End Date"
                    value={dates.endDate}
                    onChange={(newValue) =>
                      setDates((prev) => ({ ...prev, endDate: newValue }))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarIcon />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            backdropFilter: "blur(10px)",
                            backgroundColor: isDark
                              ? "rgba(255, 255, 255, 0.05)"
                              : "rgba(0, 0, 0, 0.02)",
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    size="small"
                    options={[
                      { id: "all", name: "All Customers" },
                      ...customers,
                    ]}
                    value={
                      customerFilter === "all"
                        ? { id: "all", name: "All Customers" }
                        : customers.find((c) => c.id === customerFilter) || null
                    }
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    onChange={(event, newValue) =>
                      setCustomerFilter(newValue ? newValue.id : "all")
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Customer"
                        placeholder="Search by name, phone, address..."
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            backdropFilter: "blur(10px)",
                            backgroundColor: isDark
                              ? "rgba(255, 255, 255, 0.05)"
                              : "rgba(0, 0, 0, 0.02)",
                            width: "300px",
                          },
                        }}
                      />
                    )}
                    renderOption={(props, option) => {
                      if (option.id === "all") {
                        return (
                          <Box component="li" {...props} key="all">
                            <Typography
                              variant="body2"
                              sx={{ fontStyle: "italic" }}
                            >
                              {option.name}
                            </Typography>
                          </Box>
                        );
                      }
                      return (
                        <Box component="li" {...props} key={option.id}>
                          <Box sx={{ width: "100%" }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {option.name}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                              {option.contact && (
                                <Chip
                                  size="small"
                                  label={option.contact}
                                  variant="outlined"
                                  sx={{
                                    height: 20,
                                    fontSize: "0.7rem",
                                    backdropFilter: "blur(10px)",
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        </Box>
                      );
                    }}
                    filterOptions={(options, { inputValue }) => {
                      if (!inputValue) return options.slice(0, 50);
                      const searchTerm = inputValue.toLowerCase();
                      return options.filter((option) => {
                        if (option.id === "all") return true;
                        return (
                          option.name.toLowerCase().includes(searchTerm) ||
                          option.contact?.toString().includes(searchTerm)
                        );
                      });
                    }}
                    noOptionsText={
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ p: 2, textAlign: "center" }}
                      >
                        No matching customers found
                      </Typography>
                    }
                    loading={customers.length === 0}
                    loadingText="Loading customers..."
                    blurOnSelect
                    clearOnBlur={false}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Custom Table */}
          <Card
            sx={{
              borderRadius: "16px",
              overflow: "hidden",
              position: "relative",
              background: isDark
                ? "linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.2))"
                : "linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.2))",
              backdropFilter: "blur(10px)",
              border: isDark
                ? "1px solid rgba(255, 255, 255, 0.1)"
                : "1px solid rgba(0, 0, 0, 0.08)",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
            }}
          >
            {isProcessing && (
              <LinearProgress
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  zIndex: 2,
                  height: 3,
                }}
              />
            )}

            <TableContainer
              sx={{ maxHeight: "calc(100vh - 200px)", overflow: "auto" }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        backgroundColor: isDark
                          ? "rgba(30, 41, 59, 0.95)"
                          : "rgba(248, 250, 252, 0.95)",
                        fontWeight: 700,
                        color: isDark ? "#94A3B8" : "#64748B",
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        borderBottom: `2px solid ${
                          isDark
                            ? "rgba(71, 85, 105, 0.3)"
                            : "rgba(226, 232, 240, 0.4)"
                        }`,
                        py: 1.5,
                        width: 50,
                      }}
                    >
                      #
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: isDark
                          ? "rgba(30, 41, 59, 0.95)"
                          : "rgba(248, 250, 252, 0.95)",
                        fontWeight: 700,
                        color: isDark ? "#94A3B8" : "#64748B",
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        borderBottom: `2px solid ${
                          isDark
                            ? "rgba(71, 85, 105, 0.3)"
                            : "rgba(226, 232, 240, 0.4)"
                        }`,
                        py: 1.5,
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === "date"}
                        direction={orderBy === "date" ? order : "asc"}
                        onClick={() => handleSort("date")}
                      >
                        DATE
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: isDark
                          ? "rgba(30, 41, 59, 0.95)"
                          : "rgba(248, 250, 252, 0.95)",
                        fontWeight: 700,
                        color: isDark ? "#94A3B8" : "#64748B",
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        borderBottom: `2px solid ${
                          isDark
                            ? "rgba(71, 85, 105, 0.3)"
                            : "rgba(226, 232, 240, 0.4)"
                        }`,
                        py: 1.5,
                        minWidth: 250,
                      }}
                    >
                      CUSTOMER
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: isDark
                          ? "rgba(30, 41, 59, 0.95)"
                          : "rgba(248, 250, 252, 0.95)",
                        fontWeight: 700,
                        color: isDark ? "#94A3B8" : "#64748B",
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        borderBottom: `2px solid ${
                          isDark
                            ? "rgba(71, 85, 105, 0.3)"
                            : "rgba(226, 232, 240, 0.4)"
                        }`,
                        py: 1.5,
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === "billNo"}
                        direction={orderBy === "billNo" ? order : "asc"}
                        onClick={() => handleSort("billNo")}
                      >
                        BILL NO
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: isDark
                          ? "rgba(30, 41, 59, 0.95)"
                          : "rgba(248, 250, 252, 0.95)",
                        fontWeight: 700,
                        color: isDark ? "#94A3B8" : "#64748B",
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        borderBottom: `2px solid ${
                          isDark
                            ? "rgba(71, 85, 105, 0.3)"
                            : "rgba(226, 232, 240, 0.4)"
                        }`,
                        py: 1.5,
                        align: "center",
                      }}
                    >
                      ITEMS
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: isDark
                          ? "rgba(30, 41, 59, 0.95)"
                          : "rgba(248, 250, 252, 0.95)",
                        fontWeight: 700,
                        color: isDark ? "#94A3B8" : "#64748B",
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        borderBottom: `2px solid ${
                          isDark
                            ? "rgba(71, 85, 105, 0.3)"
                            : "rgba(226, 232, 240, 0.4)"
                        }`,
                        py: 1.5,
                        align: "right",
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === "amount"}
                        direction={orderBy === "amount" ? order : "asc"}
                        onClick={() => handleSort("amount")}
                      >
                        AMOUNT
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: isDark
                          ? "rgba(30, 41, 59, 0.95)"
                          : "rgba(248, 250, 252, 0.95)",
                        fontWeight: 700,
                        color: isDark ? "#94A3B8" : "#64748B",
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        borderBottom: `2px solid ${
                          isDark
                            ? "rgba(71, 85, 105, 0.3)"
                            : "rgba(226, 232, 240, 0.4)"
                        }`,
                        py: 1.5,
                        align: "center",
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === "status"}
                        direction={orderBy === "status" ? order : "asc"}
                        onClick={() => handleSort("status")}
                      >
                        STATUS
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: isDark
                          ? "rgba(30, 41, 59, 0.95)"
                          : "rgba(248, 250, 252, 0.95)",
                        fontWeight: 700,
                        color: isDark ? "#94A3B8" : "#64748B",
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        borderBottom: `2px solid ${
                          isDark
                            ? "rgba(71, 85, 105, 0.3)"
                            : "rgba(226, 232, 240, 0.4)"
                        }`,
                        py: 1.5,
                        align: "center",
                      }}
                    >
                      ACTIONS
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedTransactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              width: 80,
                              height: 80,
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background: isDark
                                ? "linear-gradient(135deg, rgba(30, 41, 59, 0.4), rgba(30, 41, 59, 0.6))"
                                : "linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.9))",
                              backdropFilter: "blur(10px)",
                            }}
                          >
                            <ReceiptIcon
                              sx={{
                                fontSize: 40,
                                color: isDark ? "#94A3B8" : "#9CA3AF",
                                opacity: 0.6,
                              }}
                            />
                          </Box>
                          <Typography
                            variant="h6"
                            sx={{
                              color: isDark ? "#A0A0A0" : "#666",
                              fontWeight: 500,
                            }}
                          >
                            {searchInput
                              ? "No transactions found"
                              : "No transactions available"}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: isDark ? "#71717A" : "#9CA3AF",
                              textAlign: "center",
                              maxWidth: 300,
                            }}
                          >
                            {searchInput
                              ? "Try adjusting your search or filters to find what you're looking for."
                              : "Transactions will appear here once you create them."}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedTransactions.map((transaction, idx) => {
                      const cancelled = isTransactionCancelled(transaction);
                      const customer = getCustomer(transaction.customer);
                      const isPaid = transaction.summary?.pendingAmount === 0;
                      const itemsCount = transaction.items?.length || 0;

                      return (
                        <TableRow
                          key={transaction.id}
                          hover
                          sx={{
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              backgroundColor: isDark
                                ? "rgba(96, 165, 250, 0.08)"
                                : "rgba(37, 99, 235, 0.04)",
                              transform: "translateY(-1px)",
                              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                              position: "relative",
                              zIndex: 1,
                            },
                            textDecoration: cancelled ? "line-through" : "none",
                            opacity: cancelled ? 0.7 : 1,
                          }}
                        >
                          <TableCell
                            sx={{ py: 1.5, fontWeight: 600, color: "#6B7280" }}
                          >
                            {page * rowsPerPage + idx + 1}
                          </TableCell>
                          <TableCell
                            sx={{
                              py: 1.5,
                              fontWeight: 600,
                              color: isDark ? "#E2E8F0" : "#1F2937",
                              fontFamily: "'SF Mono', monospace",
                            }}
                          >
                            {(() => {
                              try {
                                return format(
                                  parse(
                                    transaction.date,
                                    "dd/MM/yyyy",
                                    new Date()
                                  ),
                                  "dd MMM yyyy"
                                );
                              } catch {
                                return transaction.date;
                              }
                            })()}
                          </TableCell>
                          <TableCell sx={{ py: 1.5 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                              }}
                            >
                              <Avatar
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: "12px",
                                  background:
                                    "linear-gradient(135deg, #8B5CF6, #A78BFA)",
                                  color: "white",
                                  fontWeight: 600,
                                  fontSize: "1rem",
                                  boxShadow:
                                    "0 2px 8px rgba(139, 92, 246, 0.3)",
                                }}
                              >
                                {customer?.name?.charAt(0) || "C"}
                              </Avatar>
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600, lineHeight: 1.3 }}
                                >
                                  {customer?.name || "N/A"}
                                </Typography>
                                {customer?.contact && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "text.secondary",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "4px",
                                      fontSize: "0.7rem",
                                    }}
                                  >
                                    {customer.contact}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell
                            sx={{
                              py: 1.5,
                              fontWeight: 700,
                              color: isDark ? "#60A5FA" : "#2563EB",
                              fontFamily: "'SF Mono', monospace",
                            }}
                          >
                            {transaction.transactionDetails?.billNo || "N/A"}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 1.5 }}>
                            <Chip
                              label={`${itemsCount} item${
                                itemsCount !== 1 ? "s" : ""
                              }`}
                              size="small"
                              sx={{
                                background:
                                  "linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(167, 139, 250, 0.15))",
                                backdropFilter: "blur(10px)",
                                border: "1px solid rgba(139, 92, 246, 0.3)",
                                color: "#8B5CF6",
                                fontWeight: 600,
                                borderRadius: "8px",
                                minWidth: "70px",
                              }}
                              icon={
                                <InventoryIcon
                                  sx={{ fontSize: "14px", color: "#8B5CF6" }}
                                />
                              }
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ py: 1.5 }}>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-end",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  background:
                                    "linear-gradient(135deg, #2563EB, #3B82F6)",
                                  WebkitBackgroundClip: "text",
                                  WebkitTextFillColor: "transparent",
                                }}
                              >
                                {new Intl.NumberFormat("en-IN", {
                                  style: "currency",
                                  currency: "INR",
                                  minimumFractionDigits: 0,
                                }).format(transaction.summary?.grandTotal || 0)}
                              </Typography>
                              {transaction.summary?.pendingAmount > 0 && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#EF4444",
                                    fontSize: "0.7rem",
                                    fontWeight: 500,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "2px",
                                    mt: "2px",
                                  }}
                                >
                                  <ArrowUpwardIcon sx={{ fontSize: "10px" }} />
                                  {(
                                    (transaction.summary.pendingAmount /
                                      transaction.summary.grandTotal) *
                                    100
                                  ).toFixed(1)}
                                  % due
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="center" sx={{ py: 1.5 }}>
                            <StatusChip
                              isPaid={isPaid}
                              isCancelled={cancelled}
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ py: 1.5 }}>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 0.5,
                                justifyContent: "center",
                              }}
                            >
                              <Tooltip title="View Details" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedTransaction(transaction);
                                    setOpenModal(true);
                                  }}
                                  sx={{
                                    color: "#2563EB",
                                    backgroundColor: "rgba(37, 99, 235, 0.1)",
                                    borderRadius: "8px",
                                    padding: "6px",
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                      backgroundColor: "rgba(37, 99, 235, 0.2)",
                                      transform: "translateY(-2px)",
                                    },
                                  }}
                                >
                                  <ViewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Print Bill" arrow>
                                <IconButton
                                  size="small"
                                  // onClick={() => handlePrintBill(transaction)}
                                  onClick={() => alert("Your subscription plan does not contain bill printing feature, To add this feature to your plan, contact owner. \n\nDarshan Jain - 9974706814\nThis will cost you an additional ₹166.66/month")}
                                  sx={{
                                    color: "#10B981",
                                    backgroundColor: "rgba(16, 185, 129, 0.1)",
                                    borderRadius: "8px",
                                    padding: "6px",
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                      backgroundColor:
                                        "rgba(16, 185, 129, 0.2)",
                                      transform: "translateY(-2px)",
                                    },
                                  }}
                                >
                                  <PrintIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {!cancelled && (
                                <Tooltip title="Cancel Bill" arrow>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      setCancelModal(true, transaction)
                                    }
                                    sx={{
                                      color: "#DC2626",
                                      backgroundColor: "rgba(220, 38, 38, 0.1)",
                                      borderRadius: "8px",
                                      padding: "6px",
                                      transition: "all 0.2s ease",
                                      "&:hover": {
                                        backgroundColor:
                                          "rgba(220, 38, 38, 0.2)",
                                        transform: "translateY(-2px)",
                                      },
                                    }}
                                  >
                                    <CancelIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, 50]}
                      colSpan={8}
                      count={filteredTransactions.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={(event, newPage) => setPage(newPage)}
                      onRowsPerPageChange={(event) => {
                        setRowsPerPage(parseInt(event.target.value, 10));
                        setPage(0);
                      }}
                      ActionsComponent={TablePaginationActions}
                      sx={{
                        borderTop: `1px solid ${
                          isDark
                            ? "rgba(71, 85, 105, 0.3)"
                            : "rgba(226, 232, 240, 0.4)"
                        }`,
                        backgroundColor: isDark
                          ? "rgba(30, 41, 59, 0.8)"
                          : "rgba(248, 250, 252, 0.8)",
                      }}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Card>

          {/* Transaction Details Modal */}
          <Dialog
            open={openModal}
            onClose={() => setOpenModal(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: "24px",
                background: isDark
                  ? "linear-gradient(135deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.98) 100%)"
                  : "linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%)",
                backdropFilter: "blur(30px)",
                border: `1px solid ${alpha(
                  isDark ? "#475569" : "#e2e8f0",
                  isDark ? 0.2 : 0.3
                )}`,
                boxShadow: "0 25px 80px rgba(0, 0, 0, 0.25)",
                maxHeight: "95vh",
                overflow: "hidden",
              },
            }}
          >
            {selectedTransaction && (
              <>
                {/* Header Section */}
                <Box
                  sx={{
                    p: 2,
                    borderBottom: `1px solid ${alpha(
                      isDark ? "#475569" : "#e2e8f0",
                      isDark ? 0.2 : 0.3
                    )}`,
                    background: isDark
                      ? "linear-gradient(90deg, rgba(96, 165, 250, 0.15) 0%, rgba(59, 130, 246, 0.1) 100%)"
                      : "linear-gradient(90deg, rgba(37, 99, 235, 0.08) 0%, rgba(29, 78, 216, 0.04) 100%)",
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: "4px",
                      background:
                        "linear-gradient(90deg, #60a5fa, #3b82f6, #60a5fa)",
                      animation: "shine 3s infinite linear",
                    },
                    "@keyframes shine": {
                      "0%": { backgroundPosition: "-200px 0" },
                      "100%": { backgroundPosition: "calc(200px + 100%) 0" },
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 1,
                    }}
                  >
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          mb: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 45,
                            height: 45,
                            borderRadius: "16px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background:
                              "linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)",
                            boxShadow: "0 8px 32px rgba(96, 165, 250, 0.3)",
                          }}
                        >
                          <ReceiptIcon sx={{ fontSize: 25, color: "white" }} />
                        </Box>
                        <Box>
                          <Typography
                            variant="h4"
                            sx={{
                              fontWeight: 800,
                              color: isDark ? "#f1f5f9" : "#1e293b",
                              mb: 0.5,
                            }}
                          >
                            Transaction Details
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{
                              color: isDark ? "#94a3b8" : "#64748b",
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              fontWeight: 500,
                            }}
                          >
                            <CalendarToday sx={{ fontSize: 18 }} />
                            {format(
                              parse(
                                selectedTransaction.date,
                                "dd/MM/yyyy",
                                new Date()
                              ),
                              "EEEE, dd MMMM yyyy"
                            )}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Bill Number & Status */}
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          flexWrap: "wrap",
                          mt: 2,
                        }}
                      >
                        <Chip
                          icon={<ReceiptIcon />}
                          label={`Bill: ${
                            selectedTransaction.transactionDetails?.billNo ||
                            "N/A"
                          }`}
                          sx={{
                            backgroundColor: alpha("#3b82f6", 0.15),
                            color: "#3b82f6",
                            fontWeight: 700,
                            fontSize: "0.9rem",
                            border: `1px solid ${alpha("#3b82f6", 0.3)}`,
                            backdropFilter: "blur(10px)",
                          }}
                        />
                        <Chip
                          icon={<PersonIcon />}
                          label={`Customer: ${getCustomerName(
                            selectedTransaction.customer
                          )}`}
                          sx={{
                            backgroundColor: alpha("#10b981", 0.15),
                            color: "#10b981",
                            fontWeight: 700,
                            fontSize: "0.9rem",
                            border: `1px solid ${alpha("#10b981", 0.3)}`,
                            backdropFilter: "blur(10px)",
                          }}
                        />
                        <Chip
                          icon={
                            selectedTransaction.transactionDetails
                              ?.gstApplicable
                              ? "📋"
                              : "📄"
                          }
                          label={`GST: ${
                            selectedTransaction.transactionDetails
                              ?.gstApplicable
                              ? "Applicable"
                              : "Not Applicable"
                          }`}
                          sx={{
                            backgroundColor: selectedTransaction
                              .transactionDetails?.gstApplicable
                              ? alpha("#059669", 0.15)
                              : alpha("#6b7280", 0.15),
                            color: selectedTransaction.transactionDetails
                              ?.gstApplicable
                              ? "#059669"
                              : "#6b7280",
                            fontWeight: 600,
                            fontSize: "0.85rem",
                            border: `1px solid ${alpha(
                              selectedTransaction.transactionDetails
                                ?.gstApplicable
                                ? "#059669"
                                : "#6b7280",
                              0.3
                            )}`,
                            backdropFilter: "blur(10px)",
                          }}
                        />
                        <Chip
                          icon={
                            selectedTransaction.summary?.pendingAmount > 0
                              ? "⏳"
                              : "✅"
                          }
                          label={
                            selectedTransaction.summary?.pendingAmount > 0
                              ? `Pending: ₹${selectedTransaction.summary.pendingAmount.toLocaleString(
                                  "en-IN"
                                )}`
                              : "Fully Paid"
                          }
                          sx={{
                            backgroundColor:
                              selectedTransaction.summary?.pendingAmount > 0
                                ? alpha("#f59e0b", 0.15)
                                : alpha("#10b981", 0.15),
                            color:
                              selectedTransaction.summary?.pendingAmount > 0
                                ? "#f59e0b"
                                : "#10b981",
                            fontWeight: 700,
                            fontSize: "0.9rem",
                            border: `1px solid ${alpha(
                              selectedTransaction.summary?.pendingAmount > 0
                                ? "#f59e0b"
                                : "#10b981",
                              0.3
                            )}`,
                            backdropFilter: "blur(10px)",
                          }}
                        />
                      </Box>
                    </Box>

                    <IconButton
                      onClick={() => setOpenModal(false)}
                      sx={{
                        backgroundColor: isDark
                          ? "rgba(255, 255, 255, 0.1)"
                          : "rgba(0, 0, 0, 0.05)",
                        color: isDark ? "#94a3b8" : "#64748b",
                        width: 48,
                        height: 48,
                        borderRadius: "12px",
                        "&:hover": {
                          backgroundColor: isDark
                            ? "rgba(255, 255, 255, 0.2)"
                            : "rgba(0, 0, 0, 0.1)",
                          transform: "rotate(90deg)",
                          transition: "transform 0.3s ease",
                        },
                        transition: "all 0.2s ease",
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                </Box>

                {/* Content Section */}
                <Box sx={{ p: 3, overflow: "auto", maxHeight: "60vh" }}>
                  <Grid container spacing={3}>
                    {/* Left Column - Items Table */}
                    <Grid item xs={12} lg={8}>
                      <Paper
                        sx={{
                          p: 3,
                          borderRadius: "20px",
                          background: isDark
                            ? "linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)"
                            : "linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)",
                          backdropFilter: "blur(20px)",
                          border: `1px solid ${alpha(
                            isDark ? "#475569" : "#e2e8f0",
                            isDark ? 0.2 : 0.3
                          )}`,
                          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                          height: "100%",
                          width: "840px",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: 3,
                          }}
                        >
                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 800,
                              color: isDark ? "#f1f5f9" : "#1e293b",
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <Box
                              sx={{
                                width: 48,
                                height: 48,
                                borderRadius: "14px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background:
                                  "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                                boxShadow: "0 8px 25px rgba(16, 185, 129, 0.3)",
                              }}
                            >
                              <InventoryIcon
                                sx={{ fontSize: 24, color: "white" }}
                              />
                            </Box>
                            Items Purchased (
                            {selectedTransaction.items?.length || 0})
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            borderRadius: "16px",
                            overflow: "hidden",
                            border: `1px solid ${alpha(
                              isDark ? "#475569" : "#e2e8f0",
                              isDark ? 0.2 : 0.3
                            )}`,
                            background: isDark
                              ? "rgba(15, 23, 42, 0.5)"
                              : "rgba(255, 255, 255, 0.7)",
                            backdropFilter: "blur(10px)",
                          }}
                        >
                          <TableContainer sx={{ maxHeight: 400 }}>
                            <Table stickyHeader size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      backgroundColor: isDark
                                        ? "rgba(30, 41, 59, 0.9)"
                                        : "rgba(248, 250, 252, 0.95)",
                                      color: isDark ? "#94a3b8" : "#64748b",
                                      fontWeight: 700,
                                      fontSize: "0.875rem",
                                      borderBottom: `2px solid ${alpha(
                                        isDark ? "#475569" : "#e2e8f0",
                                        isDark ? 0.3 : 0.4
                                      )}`,
                                      py: 1.5,
                                    }}
                                  >
                                    #
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      backgroundColor: isDark
                                        ? "rgba(30, 41, 59, 0.9)"
                                        : "rgba(248, 250, 252, 0.95)",
                                      color: isDark ? "#94a3b8" : "#64748b",
                                      fontWeight: 700,
                                      fontSize: "0.875rem",
                                      borderBottom: `2px solid ${alpha(
                                        isDark ? "#475569" : "#e2e8f0",
                                        isDark ? 0.3 : 0.4
                                      )}`,
                                    }}
                                  >
                                    Item Name
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      backgroundColor: isDark
                                        ? "rgba(30, 41, 59, 0.9)"
                                        : "rgba(248, 250, 252, 0.95)",
                                      color: isDark ? "#94a3b8" : "#64748b",
                                      fontWeight: 700,
                                      fontSize: "0.875rem",
                                      borderBottom: `2px solid ${alpha(
                                        isDark ? "#475569" : "#e2e8f0",
                                        isDark ? 0.3 : 0.4
                                      )}`,
                                    }}
                                  >
                                    HUID
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{
                                      backgroundColor: isDark
                                        ? "rgba(30, 41, 59, 0.9)"
                                        : "rgba(248, 250, 252, 0.95)",
                                      color: isDark ? "#94a3b8" : "#64748b",
                                      fontWeight: 700,
                                      fontSize: "0.875rem",
                                      borderBottom: `2px solid ${alpha(
                                        isDark ? "#475569" : "#e2e8f0",
                                        isDark ? 0.3 : 0.4
                                      )}`,
                                    }}
                                  >
                                    Weight (g)
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{
                                      backgroundColor: isDark
                                        ? "rgba(30, 41, 59, 0.9)"
                                        : "rgba(248, 250, 252, 0.95)",
                                      color: isDark ? "#94a3b8" : "#64748b",
                                      fontWeight: 700,
                                      fontSize: "0.875rem",
                                      borderBottom: `2px solid ${alpha(
                                        isDark ? "#475569" : "#e2e8f0",
                                        isDark ? 0.3 : 0.4
                                      )}`,
                                    }}
                                  >
                                    Rate
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{
                                      backgroundColor: isDark
                                        ? "rgba(30, 41, 59, 0.9)"
                                        : "rgba(248, 250, 252, 0.95)",
                                      color: isDark ? "#94a3b8" : "#64748b",
                                      fontWeight: 700,
                                      fontSize: "0.875rem",
                                      borderBottom: `2px solid ${alpha(
                                        isDark ? "#475569" : "#e2e8f0",
                                        isDark ? 0.3 : 0.4
                                      )}`,
                                    }}
                                  >
                                    Wastage %
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{
                                      backgroundColor: isDark
                                        ? "rgba(30, 41, 59, 0.9)"
                                        : "rgba(248, 250, 252, 0.95)",
                                      color: isDark ? "#94a3b8" : "#64748b",
                                      fontWeight: 700,
                                      fontSize: "0.875rem",
                                      borderBottom: `2px solid ${alpha(
                                        isDark ? "#475569" : "#e2e8f0",
                                        isDark ? 0.3 : 0.4
                                      )}`,
                                    }}
                                  >
                                    Amount
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {selectedTransaction.items?.map(
                                  (item, index) => (
                                    <TableRow
                                      key={index}
                                      hover
                                      sx={{
                                        "&:hover": {
                                          backgroundColor: isDark
                                            ? "rgba(255, 255, 255, 0.05)"
                                            : "rgba(0, 0, 0, 0.02)",
                                          transform: "translateY(-2px)",
                                          transition: "all 0.2s ease",
                                        },
                                        transition: "all 0.2s ease",
                                        position: "relative",
                                        "&::after": {
                                          content: '""',
                                          position: "absolute",
                                          bottom: 0,
                                          left: 0,
                                          right: 0,
                                          height: 1,
                                          background: isDark
                                            ? "linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent)"
                                            : "linear-gradient(90deg, transparent, rgba(0,0,0,0.03), transparent)",
                                        },
                                      }}
                                    >
                                      <TableCell sx={{ py: 1.5 }}>
                                        <Box
                                          sx={{
                                            width: 28,
                                            height: 28,
                                            borderRadius: "8px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            background: alpha("#3b82f6", 0.1),
                                            color: "#3b82f6",
                                            fontWeight: 700,
                                            fontSize: "0.875rem",
                                          }}
                                        >
                                          {index + 1}
                                        </Box>
                                      </TableCell>
                                      <TableCell>
                                        <Box>
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontWeight: 600,
                                              color: isDark
                                                ? "#f1f5f9"
                                                : "#1e293b",
                                            }}
                                          >
                                            {item.name}
                                          </Typography>
                                          {item.category && (
                                            <Chip
                                              label={item.category}
                                              size="small"
                                              sx={{
                                                mt: 0.5,
                                                backgroundColor: alpha(
                                                  "#10b981",
                                                  0.1
                                                ),
                                                color: "#10b981",
                                                fontSize: "0.625rem",
                                                height: 20,
                                                border: `1px solid ${alpha(
                                                  "#10b981",
                                                  0.2
                                                )}`,
                                              }}
                                            />
                                          )}
                                        </Box>
                                      </TableCell>
                                      <TableCell>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            color: isDark
                                              ? "#94a3b8"
                                              : "#64748b",
                                            fontFamily: "monospace",
                                          }}
                                        >
                                          {item.huid || "-"}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontWeight: 600,
                                            color: isDark
                                              ? "#f1f5f9"
                                              : "#1e293b",
                                          }}
                                        >
                                          {item.weight}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Box
                                          sx={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                            backgroundColor: alpha(
                                              "#f59e0b",
                                              0.1
                                            ),
                                            padding: "2px 8px",
                                            borderRadius: "6px",
                                            border: `1px solid ${alpha(
                                              "#f59e0b",
                                              0.2
                                            )}`,
                                          }}
                                        >
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontWeight: 600,
                                              color: "#f59e0b",
                                            }}
                                          >
                                            ₹{item.metalRate}
                                          </Typography>
                                        </Box>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontWeight: 600,
                                            color: isDark
                                              ? "#f1f5f9"
                                              : "#1e293b",
                                          }}
                                        >
                                          {item.wastage}%
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Box
                                          sx={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                            backgroundColor: alpha(
                                              "#10b981",
                                              0.1
                                            ),
                                            padding: "2px 8px",
                                            borderRadius: "6px",
                                            border: `1px solid ${alpha(
                                              "#10b981",
                                              0.2
                                            )}`,
                                          }}
                                        >
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontWeight: 700,
                                              color: "#10b981",
                                            }}
                                          >
                                            ₹
                                            {item.total.toLocaleString("en-IN")}
                                          </Typography>
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  )
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      </Paper>
                    </Grid>
                    {/* Right Column - Financial Summary */}
                    <Grid item xs={12} lg={5}>
                      <Box
                        sx={{ display: "flex", flexDirection: "row", gap: 3 }}
                      >
                        {/* Payment Summary Card */}
                        <Paper
                          sx={{
                            p: 3,
                            borderRadius: "20px",
                            background: isDark
                              ? "linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)"
                              : "linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)",
                            backdropFilter: "blur(20px)",
                            border: `1px solid ${alpha(
                              isDark ? "#475569" : "#e2e8f0",
                              isDark ? 0.2 : 0.3
                            )}`,
                            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                            width: "400px",
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              mb: 3,
                              fontWeight: 800,
                              color: isDark ? "#f1f5f9" : "#1e293b",
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: "12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background:
                                  "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)",
                                boxShadow: "0 4px 20px rgba(59, 130, 246, 0.3)",
                              }}
                            >
                              <PaymentIcon
                                sx={{ fontSize: 20, color: "white" }}
                              />
                            </Box>
                            Payment Summary
                          </Typography>

                          <Stack spacing={2}>
                            {/* Regular Payments */}
                            {selectedTransaction.paymentDetails &&
                              Object.entries(
                                selectedTransaction.paymentDetails
                              ).map(([key, value]) => {
                                if (
                                  key === "advancePayment" ||
                                  key === "totalPaymentDone" ||
                                  key === "discount"
                                )
                                  return null;

                                return (
                                  <Box
                                    key={key}
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      py: 1.5,
                                      px: 2,
                                      borderRadius: "10px",
                                      background: isDark
                                        ? "rgba(255, 255, 255, 0.05)"
                                        : "rgba(0, 0, 0, 0.02)",
                                      border: `1px solid ${alpha(
                                        isDark ? "#475569" : "#e2e8f0",
                                        isDark ? 0.1 : 0.2
                                      )}`,
                                      transition: "all 0.2s ease",
                                      "&:hover": {
                                        background: isDark
                                          ? "rgba(255, 255, 255, 0.08)"
                                          : "rgba(0, 0, 0, 0.04)",
                                        transform: "translateX(4px)",
                                      },
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: 32,
                                          height: 32,
                                          borderRadius: "8px",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          background: alpha("#3b82f6", 0.1),
                                          border: `1px solid ${alpha(
                                            "#3b82f6",
                                            0.2
                                          )}`,
                                        }}
                                      >
                                        {key === "cash" && (
                                          <LocalAtmIcon
                                            sx={{
                                              fontSize: 16,
                                              color: "#3b82f6",
                                            }}
                                          />
                                        )}
                                        {key === "cheque" && (
                                          <AccountBalanceIcon
                                            sx={{
                                              fontSize: 16,
                                              color: "#3b82f6",
                                            }}
                                          />
                                        )}
                                        {key === "online" && (
                                          <PaidIcon
                                            sx={{
                                              fontSize: 16,
                                              color: "#3b82f6",
                                            }}
                                          />
                                        )}
                                        {!["cash", "cheque", "online"].includes(
                                          key
                                        ) && (
                                          <PaymentIcon
                                            sx={{
                                              fontSize: 16,
                                              color: "#3b82f6",
                                            }}
                                          />
                                        )}
                                      </Box>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: 600,
                                          color: isDark ? "#f1f5f9" : "#1e293b",
                                          textTransform: "capitalize",
                                        }}
                                      >
                                        {key.replace(/([A-Z])/g, " $1").trim()}
                                      </Typography>
                                    </Box>
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontWeight: 700,
                                        color: "#10b981",
                                        fontSize: "1rem",
                                      }}
                                    >
                                      ₹{value.toLocaleString("en-IN")}
                                    </Typography>
                                  </Box>
                                );
                              })}

                            {/* Discount */}
                            {selectedTransaction.paymentDetails?.discount >
                              0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  py: 1.5,
                                  px: 2,
                                  borderRadius: "10px",
                                  background:
                                    "linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)",
                                  border: `1px solid ${alpha("#10b981", 0.2)}`,
                                  mt: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      borderRadius: "8px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      background: alpha("#10b981", 0.15),
                                      border: `1px solid ${alpha(
                                        "#10b981",
                                        0.3
                                      )}`,
                                    }}
                                  >
                                    <DiscountIcon
                                      sx={{ fontSize: 16, color: "#10b981" }}
                                    />
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: "#10b981" }}
                                  >
                                    Discount
                                  </Typography>
                                </Box>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontWeight: 700,
                                    color: "#10b981",
                                    fontSize: "1rem",
                                  }}
                                >
                                  -₹
                                  {selectedTransaction.paymentDetails.discount.toLocaleString(
                                    "en-IN"
                                  )}
                                </Typography>
                              </Box>
                            )}

                            {/* Advance Payment Section */}
                            {selectedTransaction.paymentDetails?.advancePayment
                              ?.enabled && (
                              <Box
                                sx={{
                                  mt: 2,
                                  pt: 2,
                                  borderTop: `1px solid ${alpha(
                                    "#f59e0b",
                                    0.2
                                  )}`,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    mb: 2,
                                    p: 1.5,
                                    borderRadius: "10px",
                                    background:
                                      "linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.05) 100%)",
                                    border: `1px solid ${alpha(
                                      "#f59e0b",
                                      0.2
                                    )}`,
                                  }}
                                >
                                  <TrendingUpIcon
                                    sx={{ fontSize: 20, color: "#f59e0b" }}
                                  />
                                  <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: 700, color: "#f59e0b" }}
                                  >
                                    Advance Payment
                                  </Typography>
                                  <Chip
                                    label={`₹${
                                      selectedTransaction.paymentDetails.advancePayment.total?.toLocaleString(
                                        "en-IN"
                                      ) || 0
                                    }`}
                                    size="small"
                                    sx={{
                                      backgroundColor: alpha("#f59e0b", 0.2),
                                      color: "#f59e0b",
                                      fontWeight: 700,
                                      border: `1px solid ${alpha(
                                        "#f59e0b",
                                        0.3
                                      )}`,
                                    }}
                                  />
                                </Box>

                                {selectedTransaction.paymentDetails
                                  .advancePayment.breakup && (
                                  <Stack spacing={1.5}>
                                    {Object.entries(
                                      selectedTransaction.paymentDetails
                                        .advancePayment.breakup
                                    ).map(([breakupKey, breakupValue]) => {
                                      if (
                                        breakupKey === "customOptions" ||
                                        !breakupValue
                                      )
                                        return null;

                                      return (
                                        <Box
                                          key={breakupKey}
                                          sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            py: 1,
                                            px: 2,
                                            borderRadius: "8px",
                                            background: isDark
                                              ? "rgba(255, 255, 255, 0.03)"
                                              : "rgba(0, 0, 0, 0.01)",
                                            border: `1px solid ${alpha(
                                              "#f59e0b",
                                              0.1
                                            )}`,
                                          }}
                                        >
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: isDark
                                                ? "#94a3b8"
                                                : "#64748b",
                                              textTransform: "capitalize",
                                            }}
                                          >
                                            {breakupKey
                                              .replace(/([A-Z])/g, " $1")
                                              .trim()}
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontWeight: 600,
                                              color: "#f59e0b",
                                            }}
                                          >
                                            ₹
                                            {breakupValue.toLocaleString(
                                              "en-IN"
                                            )}
                                          </Typography>
                                        </Box>
                                      );
                                    })}
                                  </Stack>
                                )}
                              </Box>
                            )}

                            {/* Total Paid */}
                            {selectedTransaction.paymentDetails
                              ?.totalPaymentDone && (
                              <Box
                                sx={{
                                  mt: 2,
                                  pt: 2,
                                  borderTop: `1px solid ${alpha(
                                    "#10b981",
                                    0.3
                                  )}`,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    py: 1.5,
                                    px: 2,
                                    borderRadius: "10px",
                                    background:
                                      "linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.1) 100%)",
                                    border: `1px solid ${alpha(
                                      "#10b981",
                                      0.3
                                    )}`,
                                  }}
                                >
                                  <Typography
                                    variant="body1"
                                    sx={{ fontWeight: 700, color: "#10b981" }}
                                  >
                                    Total Paid
                                  </Typography>
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      fontWeight: 800,
                                      color: "#10b981",
                                    }}
                                  >
                                    ₹
                                    {selectedTransaction.paymentDetails.totalPaymentDone.toLocaleString(
                                      "en-IN"
                                    )}
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                          </Stack>
                        </Paper>

                        {/* Totals Summary Card */}
                        <Paper
                          sx={{
                            p: 3,
                            borderRadius: "20px",
                            background: isDark
                              ? "linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)"
                              : "linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)",
                            backdropFilter: "blur(20px)",
                            border: `1px solid ${alpha(
                              isDark ? "#475569" : "#e2e8f0",
                              isDark ? 0.2 : 0.3
                            )}`,
                            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                            width: "400px",
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              mb: 3,
                              fontWeight: 800,
                              color: isDark ? "#f1f5f9" : "#1e293b",
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: "12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background:
                                  "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)",
                                boxShadow: "0 4px 20px rgba(139, 92, 246, 0.3)",
                              }}
                            >
                              <TrendingUpIcon
                                sx={{ fontSize: 20, color: "white" }}
                              />
                            </Box>
                            Financial Summary
                          </Typography>

                          <Stack spacing={2}>
                            {/* Subtotal */}
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                py: 1.5,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ color: isDark ? "#94a3b8" : "#64748b" }}
                              >
                                Subtotal
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 600 }}
                              >
                                ₹
                                {selectedTransaction.calculatedTotals?.subtotal?.toLocaleString(
                                  "en-IN"
                                ) || 0}
                              </Typography>
                            </Box>

                            {/* GST if applicable - Updated to handle IGST */}
                            {selectedTransaction.transactionDetails
                              ?.gstApplicable && (
                              <>
                                {/* Check if this is an IGST transaction */}
                                {selectedTransaction.summary?.igst ? (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      py: 1.5,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: isDark ? "#94a3b8" : "#64748b",
                                      }}
                                    >
                                      IGST (3%)
                                    </Typography>
                                    <Typography
                                      variant="body1"
                                      sx={{ fontWeight: 600, color: "#8b5cf6" }}
                                    >
                                      ₹
                                      {selectedTransaction.summary?.igst?.toLocaleString(
                                        "en-IN"
                                      ) || 0}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <>
                                    {/* CGST and SGST for intra-state */}
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        py: 1.5,
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: isDark ? "#94a3b8" : "#64748b",
                                        }}
                                      >
                                        CGST (1.5%)
                                      </Typography>
                                      <Typography
                                        variant="body1"
                                        sx={{
                                          fontWeight: 600,
                                          color: "#ef4444",
                                        }}
                                      >
                                        ₹
                                        {selectedTransaction.summary?.cgst?.toLocaleString(
                                          "en-IN"
                                        ) || 0}
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        py: 1.5,
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: isDark ? "#94a3b8" : "#64748b",
                                        }}
                                      >
                                        SGST (1.5%)
                                      </Typography>
                                      <Typography
                                        variant="body1"
                                        sx={{
                                          fontWeight: 600,
                                          color: "#ef4444",
                                        }}
                                      >
                                        ₹
                                        {selectedTransaction.summary?.sgst?.toLocaleString(
                                          "en-IN"
                                        ) || 0}
                                      </Typography>
                                    </Box>
                                  </>
                                )}
                              </>
                            )}

                            {/* Divider */}
                            <Divider
                              sx={{
                                borderColor: isDark ? "#475569" : "#e2e8f0",
                              }}
                            />

                            {/* Grand Total - Highlighted */}
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                py: 2,
                                px: 2,
                                borderRadius: "12px",
                                background:
                                  "linear-gradient(135deg, rgba(37, 99, 235, 0.1) 0%, rgba(29, 78, 216, 0.05) 100%)",
                                border: `1px solid ${alpha("#2563eb", 0.2)}`,
                                mt: 1,
                              }}
                            >
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 800,
                                  color: isDark ? "#f1f5f9" : "#1e293b",
                                }}
                              >
                                Grand Total
                              </Typography>
                              <Typography
                                variant="h4"
                                sx={{
                                  fontWeight: 900,
                                  background:
                                    "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
                                  WebkitBackgroundClip: "text",
                                  WebkitTextFillColor: "transparent",
                                  backgroundClip: "text",
                                }}
                              >
                                ₹
                                {selectedTransaction.summary?.grandTotal?.toLocaleString(
                                  "en-IN"
                                ) || 0}
                              </Typography>
                            </Box>

                            {/* Pending Amount */}
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                py: 1.5,
                                px: 2,
                                borderRadius: "10px",
                                background:
                                  selectedTransaction.summary?.pendingAmount > 0
                                    ? "linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.05) 100%)"
                                    : "linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)",
                                border: `1px solid ${alpha(
                                  selectedTransaction.summary?.pendingAmount > 0
                                    ? "#ef4444"
                                    : "#10b981",
                                  0.2
                                )}`,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                {selectedTransaction.summary?.pendingAmount >
                                0 ? (
                                  <ErrorOutlineIcon
                                    sx={{ fontSize: 20, color: "#ef4444" }}
                                  />
                                ) : (
                                  <CheckCircleOutlineIcon
                                    sx={{ fontSize: 20, color: "#10b981" }}
                                  />
                                )}
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontWeight: 700,
                                    color:
                                      selectedTransaction.summary
                                        ?.pendingAmount > 0
                                        ? "#ef4444"
                                        : "#10b981",
                                  }}
                                >
                                  {selectedTransaction.summary?.pendingAmount >
                                  0
                                    ? "Pending Amount"
                                    : "Payment Status"}
                                </Typography>
                              </Box>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 800,
                                  color:
                                    selectedTransaction.summary?.pendingAmount >
                                    0
                                      ? "#ef4444"
                                      : "#10b981",
                                }}
                              >
                                {selectedTransaction.summary?.pendingAmount > 0
                                  ? "₹" +
                                    selectedTransaction.summary.pendingAmount.toLocaleString(
                                      "en-IN"
                                    )
                                  : "Fully Paid"}
                              </Typography>
                            </Box>

                            {/* Payment Progress */}
                            {selectedTransaction.summary?.pendingAmount > 0 && (
                              <Box sx={{ mt: 2 }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: isDark ? "#94a3b8" : "#64748b",
                                    mb: 1,
                                    display: "block",
                                  }}
                                >
                                  Payment Progress
                                </Typography>
                                <LinearProgress
                                  variant="determinate"
                                  value={
                                    ((selectedTransaction.summary?.grandTotal -
                                      selectedTransaction.summary
                                        ?.pendingAmount) /
                                      selectedTransaction.summary?.grandTotal) *
                                    100
                                  }
                                  sx={{
                                    height: 8,
                                    borderRadius: 4,
                                    backgroundColor: isDark
                                      ? "#374151"
                                      : "#e5e7eb",
                                    "& .MuiLinearProgress-bar": {
                                      background:
                                        "linear-gradient(90deg, #10b981, #34d399)",
                                      borderRadius: 4,
                                    },
                                  }}
                                />
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    mt: 1,
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "#10b981" }}
                                  >
                                    {(
                                      ((selectedTransaction.summary
                                        ?.grandTotal -
                                        selectedTransaction.summary
                                          ?.pendingAmount) /
                                        selectedTransaction.summary
                                          ?.grandTotal) *
                                      100
                                    ).toFixed(1)}
                                    % Paid
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "#ef4444" }}
                                  >
                                    {(
                                      (selectedTransaction.summary
                                        ?.pendingAmount /
                                        selectedTransaction.summary
                                          ?.grandTotal) *
                                      100
                                    ).toFixed(1)}
                                    % Pending
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                          </Stack>
                        </Paper>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                {/* Footer Actions */}
                {/* <DialogActions
        sx={{
          p: 3,
          borderTop: `1px solid ${alpha(
            isDark ? '#475569' : '#e2e8f0',
            isDark ? 0.2 : 0.3
          )}`,
          background: isDark
            ? 'rgba(15, 23, 42, 0.5)'
            : 'rgba(248, 250, 252, 0.5)',
          backdropFilter: 'blur(20px)',
        }}
      >
        <Button
          onClick={() => setOpenModal(false)}
          sx={{
            borderRadius: '12px',
            px: 4,
            color: isDark ? '#94a3b8' : '#64748b',
            border: `1px solid ${alpha(
              isDark ? '#475569' : '#e2e8f0',
              isDark ? 0.2 : 0.3
            )}`,
            '&:hover': {
              backgroundColor: isDark
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(0, 0, 0, 0.02)',
            },
          }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={() => handlePrintBill(selectedTransaction)}
          sx={{
            borderRadius: '12px',
            px: 4,
            fontWeight: 600,
            background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
            boxShadow: '0 8px 25px rgba(16, 185, 129, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
              boxShadow: '0 12px 32px rgba(16, 185, 129, 0.4)',
            },
          }}
        >
          Print Bill
        </Button>
      </DialogActions> */}
              </>
            )}
          </Dialog>
        </Container>
      </Box>
      <Dialog
        open={cancelModalOpen}
        onClose={handleCancelModalClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            background: isDark
              ? "linear-gradient(135deg, rgba(30, 41, 59, 0.95), rgba(30, 41, 59, 0.98))"
              : "linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.98))",
            backdropFilter: "blur(20px)",
            border: isDark
              ? "1px solid rgba(255, 255, 255, 0.1)"
              : "1px solid rgba(0, 0, 0, 0.1)",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 2,
            borderBottom: isDark
              ? "1px solid rgba(255, 255, 255, 0.1)"
              : "1px solid rgba(0, 0, 0, 0.1)",
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#DC2626" }}>
              Cancel Bill #
              {selectedCancelTransaction?.transactionDetails?.billNo}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              This action cannot be undone
            </Typography>
          </Box>
          <IconButton
            onClick={handleCancelModalClose}
            size="small"
            disabled={isCancelling}
            sx={{
              backgroundColor: "rgba(0, 0, 0, 0.05)",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.1)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ py: 3 }}>
          {selectedCancelTransaction && (
            <Grid container spacing={3}>
              {/* Bill Information */}
              <Grid item xs={12}>
                <Card
                  sx={{
                    borderRadius: "12px",
                    background: "rgba(220, 38, 38, 0.05)",
                    border: "1px solid rgba(220, 38, 38, 0.2)",
                  }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, color: "#DC2626", mb: 1 }}
                    >
                      Bill Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Bill No
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {selectedCancelTransaction.transactionDetails?.billNo}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Original Amount
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          ₹
                          {selectedCancelTransaction.summary?.grandTotal?.toLocaleString(
                            "en-IN"
                          )}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Date
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {selectedCancelTransaction.date}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Paid Amount
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, color: "#059669" }}
                        >
                          ₹
                          {selectedCancelTransaction.summary?.paymentDone?.toLocaleString(
                            "en-IN"
                          ) ||
                            selectedCancelTransaction.summary?.grandTotal?.toLocaleString(
                              "en-IN"
                            )}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Refund Amount Section */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Refund Amount
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Cash Refund (₹)"
                      type="text"
                      value={refundAmounts.cash}
                      onChange={(e) =>
                        handleRefundChange("cash", e.target.value)
                      }
                      disabled={isCancelling}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          backgroundColor: isDark
                            ? "rgba(255, 255, 255, 0.05)"
                            : "rgba(0, 0, 0, 0.02)",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Bank Transfer (₹)"
                      type="text"
                      value={refundAmounts.bank}
                      onChange={(e) =>
                        handleRefundChange("bank", e.target.value)
                      }
                      disabled={isCancelling}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          backgroundColor: isDark
                            ? "rgba(255, 255, 255, 0.05)"
                            : "rgba(0, 0, 0, 0.02)",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Card
                      sx={{
                        mt: 2,
                        borderRadius: "10px",
                        background:
                          "linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(34, 197, 94, 0.1))",
                        border: "1px solid rgba(16, 185, 129, 0.2)",
                      }}
                    >
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: "#059669" }}
                          >
                            Total Refund
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: 700, color: "#059669" }}
                          >
                            ₹{refundAmounts.total.toLocaleString("en-IN")}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>

              {/* Items to be Updated */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Items to be Updated ({cancellingItems.length})
                </Typography>
                <Box
                  sx={{
                    maxHeight: 200,
                    overflow: "auto",
                    borderRadius: "12px",
                    border: isDark
                      ? "1px solid rgba(255, 255, 255, 0.1)"
                      : "1px solid rgba(0, 0, 0, 0.1)",
                    background: isDark
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(0, 0, 0, 0.02)",
                    p: 2,
                  }}
                >
                  {cancellingItems.map((item, index) => (
                    <Box
                      key={item.id}
                      sx={{
                        p: 1.5,
                        mb: 1,
                        borderRadius: "8px",
                        backgroundColor: isDark
                          ? "rgba(255, 255, 255, 0.02)"
                          : "rgba(0, 0, 0, 0.01)",
                        border: "1px solid rgba(0, 0, 0, 0.05)",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {item.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            HUID: {item.huid} • Weight: {item.weight}g
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: "right" }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block" }}
                          >
                            Serial Number
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {item.hasConflict ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <span
                                  style={{
                                    textDecoration: "line-through",
                                    color: "#DC2626",
                                  }}
                                >
                                  {item.originalSerialNo}
                                </span>
                                <ArrowForwardIcon
                                  sx={{ fontSize: "14px", color: "#6B7280" }}
                                />
                                <span style={{ color: "#059669" }}>
                                  {item.updateData.serialNo}
                                </span>
                              </Box>
                            ) : (
                              item.originalSerialNo
                            )}
                          </Typography>
                          {item.hasConflict && (
                            <Typography
                              variant="caption"
                              color="#F59E0B"
                              sx={{ display: "block" }}
                            >
                              Conflict resolved
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Grid>

              {/* Silver Items to be Added Back to Stock */}
              {silverItemsArray.length > 0 && (
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 2, color: "#3B82F6" }}
                  >
                    Silver Items to be Added ({silverItemsArray.length} type(s))
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: 150,
                      overflow: "auto",
                      borderRadius: "12px",
                      border: "1px solid rgba(59, 130, 246, 0.3)",
                      background: "rgba(59, 130, 246, 0.05)",
                      p: 2,
                    }}
                  >
                    {silverItemsArray.map((silverGroup, index) => (
                      <Box
                        key={silverGroup.silverType}
                        sx={{
                          p: 1.5,
                          mb: 1,
                          borderRadius: "8px",
                          backgroundColor: "rgba(59, 130, 246, 0.08)",
                          border: "1px solid rgba(59, 130, 246, 0.2)",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500, color: "#1D4ED8" }}
                            >
                              {silverGroup.silverType.toUpperCase()} Silver
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: "#3B82F6" }}
                            >
                              {silverGroup.items.length} item(s) • Total Weight:{" "}
                              {silverGroup.totalWeight.toFixed(2)}g
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: "right" }}>
                            <Typography
                              variant="caption"
                              sx={{ color: "#3B82F6", display: "block" }}
                            >
                              Will be added to stock
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Grid>
              )}

              {/* Warning Message */}
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ borderRadius: "10px" }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Important Notes:
                  </Typography>
                  <Typography variant="caption" component="div">
                    • This action will mark {cancellingItems.length} jewelry
                    item(s) as available in stock
                    {silverItemsArray.length > 0 && (
                      <>
                        <br />•{" "}
                        {silverItemsArray.reduce(
                          (sum, group) => sum + group.items.length,
                          0
                        )}{" "}
                        silver item(s) ({silverItemsArray.length} type(s)) will
                        be added back to silver stock
                      </>
                    )}
                    <br />
                    • Serial numbers will be updated if conflicts exist
                    <br />
                    • Refund amount should match the original payment
                    <br />• A cancellation record will be created
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: isDark
              ? "1px solid rgba(255, 255, 255, 0.1)"
              : "1px solid rgba(0, 0, 0, 0.1)",
          }}
        >
          <Button
            onClick={handleCancelModalClose}
            disabled={isCancelling}
            sx={{
              color: "#6B7280",
              "&:hover": {
                backgroundColor: "rgba(107, 114, 128, 0.1)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmCancellation}
            disabled={isCancelling || refundAmounts.total <= 0}
            startIcon={
              isCancelling ? <CircularProgress size={16} /> : <CancelIcon />
            }
            sx={{
              background: "linear-gradient(135deg, #DC2626, #B91C1C)",
              borderRadius: "10px",
              boxShadow: "0 4px 12px rgba(220, 38, 38, 0.25)",
              fontWeight: 600,
              "&:hover": {
                background: "linear-gradient(135deg, #B91C1C, #991B1B)",
                boxShadow: "0 6px 20px rgba(220, 38, 38, 0.35)",
              },
              "&.Mui-disabled": {
                background: "rgba(220, 38, 38, 0.3)",
                color: "rgba(255, 255, 255, 0.5)",
              },
            }}
          >
            {isCancelling ? "Cancelling..." : "Confirm Cancellation"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* </Container> */}
      {/* </Box> */}
    </LocalizationProvider>
  );
};

export default React.memo(TransactionHistory);
